import React, {
  useState,
  useCallback,
  useMemo,
  useRef,
  useEffect,
} from "react";
import { Debt, Expense } from "../../lib/types";
import { plaidService } from "../../services/external/plaidService";
import CreatedViaBadge from "../common/CreatedViaBadge";
import {
  useTheme,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  TextField,
  Box,
  Chip,
  InputAdornment,
  Card,
  CardContent,
  Typography,
  ClickAwayListener,
  SelectChangeEvent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Skeleton,
  Popover,
  FormControlLabel,
  Checkbox,
  FormGroup,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import LinkIcon from "@mui/icons-material/Link";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import GroupWorkIcon from "@mui/icons-material/GroupWork";
import PaletteIcon from "@mui/icons-material/Palette";
import ShareIcon from "@mui/icons-material/Share";
// Table header icons
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CategoryIcon from "@mui/icons-material/Category";
import GradeIcon from "@mui/icons-material/Grade";
import BusinessIcon from "@mui/icons-material/Business";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import SpeedIcon from "@mui/icons-material/Speed";
import SavingsIcon from "@mui/icons-material/Savings";
import DonutLargeIcon from "@mui/icons-material/DonutLarge";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import EventIcon from "@mui/icons-material/Event";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import RouteIcon from "@mui/icons-material/Route";
import AssessmentIcon from "@mui/icons-material/Assessment";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
// Quality and Status icons
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import BlockIcon from "@mui/icons-material/Block";
import AcUnitIcon from "@mui/icons-material/AcUnit";
import {
  ArcElement,
  Chart as ChartJS,
  Legend,
  Plugin,
  Tooltip as ChartTooltip,
} from "chart.js";
import { Pie } from "react-chartjs-2";

ChartJS.register(ArcElement, ChartTooltip, Legend);

interface DebtsListProps {
  debts: Debt[];
  expenses?: Expense[];
  onEditDebt: (debt: Debt) => void;
  onDeleteDebt: (id: string) => void;
  onLinkAccount: (debt: Debt) => void;
  onUpdateDebt?: (debt: Debt) => void;
  onShareDebt?: (debt: Debt) => void;
  plaidLoading?: boolean;
  readOnly?: boolean;
}

// Editable cell types
type EditableCellType = "text" | "currency" | "select" | "date";

interface EditableCellProps {
  value: string | number | undefined;
  type: EditableCellType;
  options?: string[];
  onSave: (value: string | number) => void;
  disabled?: boolean;
  isDarkMode: boolean;
  formatDisplay?: (value: string | number | undefined) => React.ReactNode;
  onReset?: () => void;
  resetTooltip?: string;
}

const EditableCell: React.FC<EditableCellProps> = ({
  value,
  type,
  options = [],
  onSave,
  disabled = false,
  isDarkMode,
  formatDisplay,
  onReset,
  resetTooltip = "Reset to default",
}) => {
  const negativeColor = isDarkMode ? "#fc8181" : "#e53e3e";
  const positiveColor = isDarkMode ? "#68d391" : "#38a169";
  const [isEditing, setIsEditing] = useState(false);
  const [selectOpen, setSelectOpen] = useState(false);
  const [editValue, setEditValue] = useState<string>(String(value ?? ""));
  const inputRef = useRef<HTMLInputElement>(null);

  const normalizeDateInput = (rawValue: string) => {
    if (!rawValue) return "";
    if (rawValue.includes("T")) return rawValue.split("T")[0];
    if (rawValue.includes(" ")) return rawValue.split(" ")[0];
    return rawValue;
  };

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  // Open select dropdown after entering edit mode
  useEffect(() => {
    if (isEditing && type === "select") {
      // Small delay to ensure the Select is mounted before opening
      const timer = setTimeout(() => setSelectOpen(true), 50);
      return () => clearTimeout(timer);
    }
  }, [isEditing, type]);

  const handleStartEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (disabled) return;
    console.log("EditableCell start edit", { type, value });
    // For currency, show just the number without formatting
    if (type === "currency") {
      setEditValue(String(value ?? 0));
    } else if (type === "date") {
      setEditValue(normalizeDateInput(String(value ?? "")));
    } else {
      setEditValue(String(value ?? ""));
    }
    setIsEditing(true);
  };

  const handleSave = () => {
    let saveValue: string | number = editValue;
    if (type === "currency") {
      // Remove currency formatting and parse as number
      const numValue = parseFloat(editValue.replace(/[^0-9.-]/g, ""));
      saveValue = isNaN(numValue) ? 0 : numValue;
    }
    console.log("EditableCell save", { type, editValue, saveValue });
    onSave(saveValue);
    setSelectOpen(false);
    setIsEditing(false);
  };

  const handleCancel = () => {
    console.log("EditableCell cancel", { type, value });
    setEditValue(String(value ?? ""));
    setSelectOpen(false);
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSave();
    } else if (e.key === "Escape") {
      handleCancel();
    }
  };

  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    const newValue = e.target.value;
    console.log("EditableCell select change", { type, newValue });
    onSave(newValue);
    setSelectOpen(false);
    setIsEditing(false);
  };

  // Show "-" for empty/null/undefined values to ensure clickable area
  const rawValue =
    value === "" || value === null || value === undefined ? "-" : value;
  const displayValue: React.ReactNode = formatDisplay
    ? formatDisplay(value)
    : String(rawValue);

  if (isEditing) {
    if (type === "select") {
      return (
        <FormControl size="small" sx={{ minWidth: 100 }}>
          <Select
            value={editValue}
            onChange={handleSelectChange}
            open={selectOpen}
            onOpen={() => setSelectOpen(true)}
            onClose={handleCancel}
            onKeyDown={handleKeyDown}
            sx={{ fontSize: "13px" }}
            MenuProps={{
              disablePortal: true,
              anchorOrigin: {
                vertical: "bottom",
                horizontal: "left",
              },
              transformOrigin: {
                vertical: "top",
                horizontal: "left",
              },
            }}
          >
            {options.map((opt) => (
              <MenuItem key={opt} value={opt}>
                {opt}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }

    return (
      <ClickAwayListener onClickAway={handleSave}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <TextField
            inputRef={inputRef}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            size="small"
            type={
              type === "currency" ? "text" : type === "date" ? "date" : "text"
            }
            inputProps={{
              style: { fontSize: "13px", padding: "4px 8px" },
              inputMode: type === "currency" ? "decimal" : undefined,
            }}
            sx={{ width: "100%" }}
          />
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <IconButton size="small" onClick={handleSave} sx={{ p: 0.25 }}>
              <CheckIcon sx={{ fontSize: 16, color: positiveColor }} />
            </IconButton>
            <IconButton size="small" onClick={handleCancel} sx={{ p: 0.25 }}>
              <CloseIcon sx={{ fontSize: 16, color: negativeColor }} />
            </IconButton>
            {onReset && (
              <Tooltip title={resetTooltip}>
                <IconButton
                  size="small"
                  onClick={() => {
                    onReset();
                    setIsEditing(false);
                  }}
                  sx={{ p: 0.25 }}
                >
                  <RestartAltIcon sx={{ fontSize: 16, color: "#3182ce" }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>
      </ClickAwayListener>
    );
  }

  return (
    <span
      onClick={handleStartEdit}
      style={{
        cursor: disabled ? "default" : "pointer",
        padding: "2px 4px",
        borderRadius: "4px",
        transition: "background-color 0.2s",
        display: "inline-block",
      }}
      onMouseEnter={(e) => {
        if (!disabled) {
          e.currentTarget.style.backgroundColor = isDarkMode
            ? "rgba(255,255,255,0.1)"
            : "rgba(0,0,0,0.05)";
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = "transparent";
      }}
      title={disabled ? undefined : "Click to edit"}
    >
      {displayValue}
    </span>
  );
};

// Define type for sortable columns (including calculated columns)
type SortableDebtKey = Exclude<keyof Debt, "isShared" | "canDefault">;
type SortKey = SortableDebtKey | "available" | "utilization";
type SortDirection = "asc" | "desc";

// Status and type options for dropdowns
const STATUS_OPTIONS = ["Active", "Paid Off", "Closed", "Frozen"];
const TYPE_OPTIONS = [
  "Credit Card",
  "Auto Loan",
  "Revolving",
  "Business Loan",
  "Mortgage",
  "Personal Loan",
  "Student Loan",
  "Medical",
  "Taxes",
  "Other",
];
const PAYOFF_STRATEGY_OPTIONS = [
  "Minimum",
  "Aggressive",
  "Balance Transfer",
  "Consolidation",
  "Snowball",
  "Avalanche",
];
const CREDIT_AFFECTING_OPTIONS = [
  "Spencer",
  "Brittany",
  "Spencer & Brittany",
  "No",
  "",
];
const DEBT_QUALITY_OPTIONS = ["good", "bad", ""];
const CAN_DEFAULT_OPTIONS = ["cant", "can"];

// Column definitions for visibility toggle
type ColumnKey =
  | "status"
  | "type"
  | "quality"
  | "canDefault"
  | "creditor"
  | "account"
  | "balance"
  | "limit"
  | "available"
  | "utilization"
  | "paydown"
  | "monthly"
  | "dueDate"
  | "dateBorrowed"
  | "credits"
  | "payoffStrategy"
  | "creditAffecting"
  | "actions";

interface ColumnDef {
  key: ColumnKey;
  label: string;
  width: string;
}

const COLUMN_DEFINITIONS: ColumnDef[] = [
  { key: "status", label: "Status", width: "5%" },
  { key: "type", label: "Type", width: "5%" },
  { key: "quality", label: "Quality", width: "5%" },
  { key: "canDefault", label: "Can Default", width: "6%" },
  { key: "creditor", label: "Creditor", width: "10%" },
  { key: "account", label: "Account", width: "8%" },
  { key: "balance", label: "Balance", width: "7%" },
  { key: "limit", label: "Limit", width: "6%" },
  { key: "available", label: "Available", width: "6%" },
  { key: "utilization", label: "Utilization", width: "7%" },
  { key: "paydown", label: "Paydown", width: "7%" },
  { key: "monthly", label: "Monthly", width: "7%" },
  { key: "dueDate", label: "Due Date", width: "5%" },
  { key: "dateBorrowed", label: "Date Borrowed", width: "5%" },
  { key: "credits", label: "Credits", width: "6%" },
  { key: "payoffStrategy", label: "Payoff Strategy", width: "10%" },
  { key: "creditAffecting", label: "Credit Affecting", width: "10%" },
  { key: "actions", label: "Actions", width: "130px" },
];

const COLUMN_VISIBILITY_STORAGE_KEY = "debtColumnVisibility";
const TOTAL_BALANCE_EXCLUDED_TYPES_KEY = "debtTotalExcludedTypes";
const DEFAULT_TOTAL_BALANCE_EXCLUDED_TYPES = ["Auto Loan", "Mortgage"];
const MONTHLY_PAYMENTS_EXCLUDED_TYPES_KEY = "debtMonthlyPaymentsExcludedTypes";
const DEFAULT_MONTHLY_PAYMENTS_EXCLUDED_TYPES = [
  "Mortgage",
  "Auto Loan",
  "Taxes",
  "Revolving",
  "Personal Loans",
  "Personal Loan",
];

const DEFAULT_VISIBLE_COLUMNS: ColumnKey[] = [
  "status",
  "type",
  "quality",
  "canDefault",
  "creditor",
  "account",
  "balance",
  "limit",
  "available",
  "utilization",
  "paydown",
  "monthly",
  "dueDate",
  "credits",
  "payoffStrategy",
  "creditAffecting",
  "actions",
];

// Helper to get ordinal suffix for a number
const getOrdinalSuffix = (n: number): string => {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return s[(v - 20) % 10] || s[v] || s[0];
};

// Helper to format due date with ordinal suffix
const formatDueDate = (value: string | number | undefined): string => {
  if (!value || value === "" || value === "-") return "—";
  const strValue = String(value).trim().toUpperCase();
  if (strValue === "N/A") return "N/A";

  // Extract just the number, removing any existing suffix
  const numMatch = strValue.match(/^(\d+)/);
  if (numMatch) {
    const num = parseInt(numMatch[1], 10);
    if (num >= 1 && num <= 31) {
      return `${num}${getOrdinalSuffix(num)}`;
    }
  }
  return "—";
};

const PIE_COLORS = [
  "#3182CE",
  "#805AD5",
  "#38A169",
  "#DD6B20",
  "#D53F8C",
  "#319795",
  "#E53E3E",
  "#718096",
];

const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const GOOD_DEBT_TYPE_KEYS = new Set([
  "mortgage",
  "student loan",
  "business loan",
  "real estate loan",
  "investment loan",
]);

const classifyDebtQuality = (debt: Debt): "Good Debt" | "Bad Debt" => {
  if (debt.debtQuality === "good") return "Good Debt";
  if (debt.debtQuality === "bad") return "Bad Debt";
  const typeKey = String(debt.type || "")
    .trim()
    .toLowerCase();
  return GOOD_DEBT_TYPE_KEYS.has(typeKey) ? "Good Debt" : "Bad Debt";
};

const pieCalloutPlugin: Plugin<"pie"> = {
  id: "pieCalloutPlugin",
  afterDatasetsDraw(chart) {
    const dataset = chart.data.datasets[0];
    if (!dataset) return;

    const values = (dataset.data as Array<number | string>).map((v) =>
      Number(v || 0)
    );
    const total = values.reduce((sum, value) => sum + value, 0);
    if (total <= 0) return;

    const meta = chart.getDatasetMeta(0);
    const ctx = chart.ctx;
    const activeIndexes = new Set(
      chart.getActiveElements().map((element) => element.index)
    );
    if (activeIndexes.size === 0) return;

    ctx.save();
    ctx.font = "10px Inter, sans-serif";
    ctx.fillStyle = "#FFFFFF";
    ctx.strokeStyle = "#A0AEC0";
    ctx.lineWidth = 1;

    meta.data.forEach((arc, index) => {
      const value = values[index];
      if (!value || value <= 0 || !activeIndexes.has(index)) return;

      const {
        x: centerX,
        y: centerY,
        startAngle,
        endAngle,
        outerRadius,
      } = arc.getProps(["x", "y", "startAngle", "endAngle", "outerRadius"]);

      const midAngle = (startAngle + endAngle) / 2;
      const startX = centerX + Math.cos(midAngle) * outerRadius;
      const startY = centerY + Math.sin(midAngle) * outerRadius;
      const midX = centerX + Math.cos(midAngle) * (outerRadius + 14);
      const midY = centerY + Math.sin(midAngle) * (outerRadius + 14);
      const isRight = Math.cos(midAngle) >= 0;
      const endX = midX + (isRight ? 24 : -24);
      ctx.strokeStyle = "#FFFFFF";
      ctx.lineWidth = 1.75;
      ctx.beginPath();
      ctx.moveTo(startX, startY);
      ctx.lineTo(midX, midY);
      ctx.lineTo(endX, midY);
      ctx.stroke();

      const pct = (value / total) * 100;
      const label = `${pct.toFixed(1)}% • ${currencyFormatter.format(value)}`;
      ctx.textAlign = isRight ? "left" : "right";
      ctx.textBaseline = "middle";
      ctx.font = "700 10px Inter, sans-serif";
      ctx.strokeStyle = "rgba(0, 0, 0, 0.55)";
      ctx.lineWidth = 2.5;
      const labelX = endX + (isRight ? 4 : -4);
      ctx.strokeText(label, labelX, midY);
      ctx.fillStyle = "#FFFFFF";
      ctx.fillText(label, labelX, midY);
    });

    ctx.restore();
  },
};

// Helper to parse and validate due date input
const parseDueDateInput = (value: string): string => {
  const trimmed = value.trim().toUpperCase();
  if (trimmed === "N/A" || trimmed === "NA") return "N/A";

  // Extract just the number
  const numMatch = trimmed.match(/^(\d+)/);
  if (numMatch) {
    const num = parseInt(numMatch[1], 10);
    if (num >= 1 && num <= 31) {
      return String(num); // Store just the number in DB
    }
  }
  return ""; // Invalid input
};

const parseNumericAmount = (
  value: string | number | null | undefined
): number => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : 0;
  }
  if (typeof value !== "string") return 0;
  const cleaned = value.replace(/[^0-9.-]/g, "");
  const parsed = Number.parseFloat(cleaned);
  return Number.isFinite(parsed) ? parsed : 0;
};

// Helper to check if debt is a Plaid account
const isPlaidAccount = (debt: Debt): boolean => {
  if (debt.id?.startsWith("plaid-")) return true;
  const flag = (debt as Debt & { isPlaidAccount?: boolean }).isPlaidAccount;
  return flag === true;
};

const isSharedDebt = (debt: Debt): boolean => {
  return Boolean(
    (debt as Debt & { is_shared?: boolean }).is_shared || debt.isShared
  );
};

const DebtsList: React.FC<DebtsListProps> = ({
  debts,
  expenses = [],
  onEditDebt,
  onDeleteDebt,
  onLinkAccount,
  onUpdateDebt,
  onShareDebt,
  plaidLoading = false,
  readOnly = false,
}) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";
  const [sortKey, setSortKey] = useState<SortKey>("balance");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [groupByCreditor, setGroupByCreditor] = useState<boolean>(true);
  const [useColors, setUseColors] = useState<boolean>(true);
  const [plaidCustomNames, setPlaidCustomNames] = useState<
    Record<string, string>
  >({});
  const [expandedCreditors, setExpandedCreditors] = useState<Set<string>>(
    new Set()
  );
  const [linkedExpenseModal, setLinkedExpenseModal] = useState<Expense | null>(
    null
  );
  const [minIncomeBreakdownOpen, setMinIncomeBreakdownOpen] = useState(false);

  // Color helper functions
  const negativeColor = useColors ? "#e53e3e" : "inherit";
  const positiveColor = useColors ? "#38a169" : "inherit";
  const hasPlaidDebt = useMemo(
    () => debts.some((debt) => isPlaidAccount(debt)),
    [debts]
  );
  const showTotalsPlaceholder = plaidLoading && hasPlaidDebt;

  const renderPlaidPlaceholder = (width: number) => (
    <Skeleton variant="text" width={width} sx={{ display: "inline-block" }} />
  );

  const renderPlaidValue = (
    debt: Debt,
    content: React.ReactNode,
    width: number
  ) => {
    if (plaidLoading && isPlaidAccount(debt)) {
      return renderPlaidPlaceholder(width);
    }
    return content;
  };

  const renderTotalsValue = (content: React.ReactNode, width: number) => {
    if (showTotalsPlaceholder) {
      return renderPlaidPlaceholder(width);
    }
    return content;
  };

  // Toggle creditor group expansion
  const toggleCreditorExpansion = useCallback((creditor: string) => {
    setExpandedCreditors((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(creditor)) {
        newSet.delete(creditor);
      } else {
        newSet.add(creditor);
      }
      return newSet;
    });
  }, []);

  // Handle inline edit save
  const handleInlineUpdate = useCallback(
    (debt: Debt, field: keyof Debt, value: string | number | boolean) => {
      if (!onUpdateDebt) return;
      const updatedDebt = { ...debt, [field]: value };
      onUpdateDebt(updatedDebt);
    },
    [onUpdateDebt]
  );

  const getPlaidAccountId = useCallback((debt: Debt): string | null => {
    const explicitId = (
      debt as Debt & {
        plaidAccountId?: string;
      }
    ).plaidAccountId;
    if (explicitId) return explicitId;
    if (debt.id?.startsWith("plaid-")) return debt.id.replace(/^plaid-/, "");
    return null;
  }, []);

  const getPlaidDisplayName = useCallback(
    (debt: Debt): string => {
      if (!isPlaidAccount(debt)) return debt.account;
      const plaidAccountId = getPlaidAccountId(debt);
      if (!plaidAccountId) return debt.account;
      const customName = plaidCustomNames[plaidAccountId];
      return customName?.trim() ? customName.trim() : debt.account;
    },
    [getPlaidAccountId, plaidCustomNames]
  );

  const handleSavePlaidDisplayName = useCallback(
    async (debt: Debt, value: string | number) => {
      const plaidAccountId = getPlaidAccountId(debt);
      if (!plaidAccountId) return;

      const nextName = String(value ?? "").trim();
      try {
        if (!nextName) {
          await plaidService.deleteCustomName(plaidAccountId);
          setPlaidCustomNames((prev) => {
            const next = { ...prev };
            delete next[plaidAccountId];
            return next;
          });
          return;
        }

        await plaidService.setCustomName(plaidAccountId, nextName);
        setPlaidCustomNames((prev) => ({
          ...prev,
          [plaidAccountId]: nextName,
        }));
      } catch (error) {
        console.error("[Debts] Failed to save Plaid custom name:", {
          plaidAccountId,
          nextName,
          error,
        });
      }
    },
    [getPlaidAccountId]
  );

  // Handle opening linked expense modal
  const handleOpenLinkedExpense = useCallback(
    (debt: Debt) => {
      if (debt.linkedExpenseId && expenses.length > 0) {
        const linkedExpense = expenses.find(
          (e) => e.id === debt.linkedExpenseId
        );
        if (linkedExpense) {
          setLinkedExpenseModal(linkedExpense);
        }
      }
    },
    [expenses]
  );

  // Check if debt has a linked expense
  const hasLinkedExpense = useCallback(
    (debt: Debt) => {
      return (
        debt.linkedExpenseId &&
        expenses.some((e) => e.id === debt.linkedExpenseId)
      );
    },
    [expenses]
  );

  // Filter state
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [sharedOnly, setSharedOnly] = useState<boolean>(false);
  const [totalExcludedTypes, setTotalExcludedTypes] = useState<string[]>(
    DEFAULT_TOTAL_BALANCE_EXCLUDED_TYPES
  );
  const [monthlyPaymentsExcludedTypes, setMonthlyPaymentsExcludedTypes] =
    useState<string[]>(DEFAULT_MONTHLY_PAYMENTS_EXCLUDED_TYPES);

  // Column visibility state
  const [visibleColumns, setVisibleColumns] = useState<Set<ColumnKey>>(() => {
    try {
      const stored = localStorage.getItem(COLUMN_VISIBILITY_STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as ColumnKey[];
        return new Set(parsed);
      }
    } catch (error) {
      console.warn("Failed to load column visibility:", error);
    }
    return new Set(DEFAULT_VISIBLE_COLUMNS);
  });
  const [columnsAnchorEl, setColumnsAnchorEl] = useState<HTMLElement | null>(
    null
  );
  const columnsPopoverOpen = Boolean(columnsAnchorEl);
  const [totalTypesAnchorEl, setTotalTypesAnchorEl] =
    useState<HTMLElement | null>(null);
  const totalTypesPopoverOpen = Boolean(totalTypesAnchorEl);
  const [monthlyTypesAnchorEl, setMonthlyTypesAnchorEl] =
    useState<HTMLElement | null>(null);
  const monthlyTypesPopoverOpen = Boolean(monthlyTypesAnchorEl);

  useEffect(() => {
    let mounted = true;
    const loadCustomNames = async () => {
      try {
        const names = await plaidService.getCustomNames();
        if (mounted) {
          setPlaidCustomNames(names || {});
        }
      } catch (error) {
        console.error("[Debts] Failed to load Plaid custom names:", error);
      }
    };
    void loadCustomNames();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(TOTAL_BALANCE_EXCLUDED_TYPES_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as string[];
        if (Array.isArray(parsed)) {
          setTotalExcludedTypes(parsed);
        }
      }
    } catch (error) {
      console.warn("Failed to load total balance exclusions:", error);
    }
  }, []);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(MONTHLY_PAYMENTS_EXCLUDED_TYPES_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as string[];
        if (Array.isArray(parsed)) {
          setMonthlyPaymentsExcludedTypes(parsed);
        }
      }
    } catch (error) {
      console.warn("Failed to load monthly payments exclusions:", error);
    }
  }, []);

  // Toggle column visibility
  const toggleColumnVisibility = useCallback((columnKey: ColumnKey) => {
    setVisibleColumns((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(columnKey)) {
        // Don't allow hiding all columns - keep at least actions
        if (newSet.size > 1 || columnKey === "actions") {
          newSet.delete(columnKey);
        }
      } else {
        newSet.add(columnKey);
      }
      // Save to localStorage
      try {
        localStorage.setItem(
          COLUMN_VISIBILITY_STORAGE_KEY,
          JSON.stringify(Array.from(newSet))
        );
      } catch (error) {
        console.warn("Failed to save column visibility:", error);
      }
      return newSet;
    });
  }, []);

  // Check if column is visible
  const isColumnVisible = useCallback(
    (columnKey: ColumnKey) => {
      if (readOnly && columnKey === "actions") return false;
      return visibleColumns.has(columnKey);
    },
    [readOnly, visibleColumns]
  );

  const updateTotalExcludedTypes = useCallback((next: string[]) => {
    setTotalExcludedTypes(next);
    try {
      localStorage.setItem(
        TOTAL_BALANCE_EXCLUDED_TYPES_KEY,
        JSON.stringify(next)
      );
    } catch (error) {
      console.warn("Failed to save total balance exclusions:", error);
    }
    console.debug("[Debts] Updated total balance exclusions:", next);
  }, []);

  const updateMonthlyPaymentsExcludedTypes = useCallback((next: string[]) => {
    setMonthlyPaymentsExcludedTypes(next);
    try {
      localStorage.setItem(
        MONTHLY_PAYMENTS_EXCLUDED_TYPES_KEY,
        JSON.stringify(next)
      );
    } catch (error) {
      console.warn("Failed to save monthly payments exclusions:", error);
    }
    console.debug("[Debts] Updated monthly payments exclusions:", next);
  }, []);

  // Get unique values for filter dropdowns
  const uniqueStatuses = useMemo(() => {
    const statuses = new Set(debts.map((d) => d.status || "Unknown"));
    return Array.from(statuses).sort();
  }, [debts]);

  const uniqueTypes = useMemo(() => {
    const types = new Set(debts.map((d) => d.type || "Other"));
    return Array.from(types).sort();
  }, [debts]);

  // Calculate default paydown amount based on debt type
  const calculateDefaultPaydown = useCallback((debt: Debt): number => {
    const balance = Math.abs(Number(debt.balance || 0));
    const creditLimit = Number(debt.creditLimit || 0);
    const debtType = (debt.type || "").toLowerCase();

    // Credit Card: 66% of Credit Limit
    if (debtType.includes("credit card")) {
      return creditLimit * 0.66;
    }

    // Medical, Business Loan, Personal Loan, Taxes: 100% of balance
    if (
      debtType.includes("medical") ||
      debtType.includes("business loan") ||
      debtType.includes("personal loan") ||
      debtType.includes("taxes")
    ) {
      return balance;
    }

    // Default fallback: 100% of balance
    return balance;
  }, []);

  // Calculate available credit
  const calculateAvailable = (debt: Debt): number => {
    return Math.max(
      0,
      Number(debt.creditLimit || 0) - Number(debt.balance || 0)
    );
  };

  // Calculate utilization percentage
  const calculateUtilization = (debt: Debt): number => {
    const creditLimit = Number(debt.creditLimit || 0);
    if (creditLimit <= 0) return 0;
    return Math.round(
      (Math.abs(Number(debt.balance || 0)) / creditLimit) * 100
    );
  };

  // Filter debts based on filter criteria
  const filteredDebts = useMemo(() => {
    return debts.filter((debt) => {
      // Status filter
      if (statusFilter !== "all" && debt.status !== statusFilter) {
        return false;
      }

      // Type filter
      if (typeFilter !== "all" && (debt.type || "Other") !== typeFilter) {
        return false;
      }

      // Search query (search in account, creditor, type)
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        const matchesAccount = debt.account?.toLowerCase().includes(query);
        const matchesCreditor = debt.creditor?.toLowerCase().includes(query);
        const matchesType = debt.type?.toLowerCase().includes(query);
        if (!matchesAccount && !matchesCreditor && !matchesType) {
          return false;
        }
      }

      if (sharedOnly && !isSharedDebt(debt)) {
        return false;
      }

      return true;
    });
  }, [debts, statusFilter, typeFilter, searchQuery, sharedOnly]);

  const totalExcludedTypeSet = useMemo(
    () => new Set(totalExcludedTypes.map((type) => type.toLowerCase())),
    [totalExcludedTypes]
  );
  const monthlyPaymentsExcludedTypeSet = useMemo(
    () =>
      new Set(monthlyPaymentsExcludedTypes.map((type) => type.toLowerCase())),
    [monthlyPaymentsExcludedTypes]
  );

  const totalBalanceDebts = useMemo(
    () =>
      filteredDebts.filter(
        (debt) =>
          !totalExcludedTypeSet.has((debt.type || "Other").toLowerCase())
      ),
    [filteredDebts, totalExcludedTypeSet]
  );
  const monthlyPaymentsDebts = useMemo(
    () =>
      filteredDebts.filter(
        (debt) =>
          !monthlyPaymentsExcludedTypeSet.has(
            (debt.type || "Other").toLowerCase()
          )
      ),
    [filteredDebts, monthlyPaymentsExcludedTypeSet]
  );

  // Sort debts based on current sort key and direction
  const sortedDebts = [...filteredDebts].sort((a, b) => {
    let aValue: string | number | undefined;
    let bValue: string | number | undefined;

    // Handle calculated columns
    if (sortKey === "available") {
      aValue = calculateAvailable(a);
      bValue = calculateAvailable(b);
    } else if (sortKey === "utilization") {
      aValue = calculateUtilization(a);
      bValue = calculateUtilization(b);
    } else if (sortKey === "maxMonthlyPayment") {
      aValue = parseNumericAmount(a.maxMonthlyPayment);
      bValue = parseNumericAmount(b.maxMonthlyPayment);
    } else {
      aValue = a[sortKey as SortableDebtKey] as string | number | undefined;
      bValue = b[sortKey as SortableDebtKey] as string | number | undefined;
    }

    // Handle different types of values
    if (typeof aValue === "string" && typeof bValue === "string") {
      return sortDirection === "asc"
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }

    if (typeof aValue === "number" && typeof bValue === "number") {
      return sortDirection === "asc" ? aValue - bValue : bValue - aValue;
    }

    // Fallback for mixed or undefined values
    return 0;
  });

  // Calculate totals (based on filtered debts)
  const totalBalanceAll = filteredDebts.reduce(
    (sum, debt) => sum + Math.abs(Number(debt.balance || 0)),
    0
  );
  const totalBalanceCard = totalBalanceDebts.reduce(
    (sum, debt) => sum + Math.abs(Number(debt.balance || 0)),
    0
  );
  const totalBalanceUnfiltered = debts.reduce(
    (sum, debt) => sum + Math.abs(Number(debt.balance || 0)),
    0
  );
  const totalCreditLimit = filteredDebts.reduce(
    (sum, debt) => sum + Number(debt.creditLimit || 0),
    0
  );
  const totalAvailable = filteredDebts.reduce(
    (sum, debt) =>
      sum +
      Math.max(
        0,
        Number(debt.creditLimit || 0) - Math.abs(Number(debt.balance || 0))
      ),
    0
  );
  const totalUsedCredit = filteredDebts.reduce((sum, debt) => {
    const limit = Number(debt.creditLimit || 0);
    if (limit <= 0) return sum;
    const used = Math.abs(Number(debt.balance || 0));
    return sum + Math.min(used, limit);
  }, 0);
  const utilizationUsedPct =
    totalCreditLimit > 0
      ? Math.min(100, (totalUsedCredit / totalCreditLimit) * 100)
      : 0;
  const utilizationAvailablePct = Math.max(0, 100 - utilizationUsedPct);
  const { averageUtilization, lowestUtilization, highestUtilization } =
    useMemo(() => {
      const debtsWithLimit = filteredDebts.filter(
        (debt) => Number(debt.creditLimit || 0) > 0
      );
      if (debtsWithLimit.length === 0)
        return {
          averageUtilization: 0,
          lowestUtilization: 0,
          highestUtilization: 0,
        };

      const utilizations = debtsWithLimit.map((debt) => {
        const limit = Number(debt.creditLimit || 0);
        const balance = Math.abs(Number(debt.balance || 0));
        return Math.round((balance / limit) * 100);
      });

      const totalUtil = utilizations.reduce((sum, util) => sum + util, 0);
      const avg = Math.round(totalUtil / debtsWithLimit.length);
      const lowest = Math.min(...utilizations);
      const highest = Math.max(...utilizations);

      return {
        averageUtilization: avg,
        lowestUtilization: lowest,
        highestUtilization: highest,
      };
    }, [filteredDebts]);
  const totalPaydownAmount = filteredDebts.reduce(
    (sum, debt) =>
      sum +
      Math.abs(
        Number(
          debt.paydownAmount !== undefined && debt.paydownAmount !== null
            ? debt.paydownAmount
            : calculateDefaultPaydown(debt)
        )
      ),
    0
  );

  // Filter debts that are credit affecting (not "No", not null, not empty)
  const creditAffectingDebts = useMemo(() => {
    return filteredDebts.filter(
      (debt) =>
        debt.creditAffecting &&
        debt.creditAffecting !== "No" &&
        debt.creditAffecting !== ""
    );
  }, [filteredDebts]);

  // Filter credit card debts only
  const creditCardDebts = useMemo(() => {
    return filteredDebts.filter((debt) =>
      (debt.type || "").toLowerCase().includes("credit card")
    );
  }, [filteredDebts]);

  // Calculate paydown to 33% utilization for credit-affecting debts only
  const creditAffectingPaydownAmount = useMemo(() => {
    return creditAffectingDebts.reduce((sum, debt) => {
      const balance = Math.abs(Number(debt.balance || 0));
      const creditLimit = Number(debt.creditLimit || 0);
      const targetBalance = creditLimit * 0.33; // 33% utilization target
      const paydownNeeded = Math.max(0, balance - targetBalance);
      return sum + paydownNeeded;
    }, 0);
  }, [creditAffectingDebts]);

  // Calculate paydown for ALL debts (using existing totalPaydownAmount logic)
  const allDebtsPaydownAmount = totalPaydownAmount;

  // Calculate paydown for credit cards only (to 33% utilization)
  const creditCardsOnlyPaydownAmount = useMemo(() => {
    return creditCardDebts.reduce((sum, debt) => {
      const balance = Math.abs(Number(debt.balance || 0));
      const creditLimit = Number(debt.creditLimit || 0);
      const targetBalance = creditLimit * 0.33; // 33% utilization target
      const paydownNeeded = Math.max(0, balance - targetBalance);
      return sum + paydownNeeded;
    }, 0);
  }, [creditCardDebts]);

  // Create breakdown data for paydown tooltip
  const creditAffectingBreakdown = useMemo(() => {
    return creditAffectingDebts.map((debt) => {
      const balance = Math.abs(Number(debt.balance || 0));
      const creditLimit = Number(debt.creditLimit || 0);
      const targetBalance = creditLimit * 0.33;
      const paydownNeeded = Math.max(0, balance - targetBalance);
      return {
        creditor: debt.creditor || "Unknown",
        account: debt.account || "Unknown",
        balance: balance,
        paydown: paydownNeeded,
      };
    });
  }, [creditAffectingDebts]);

  // Create breakdown data for all cards tooltips
  const allDebtsBreakdown = useMemo(() => {
    return filteredDebts.map((debt) => ({
      creditor: debt.creditor || "Unknown",
      account: debt.account || "Unknown",
      balance: Math.abs(Number(debt.balance || 0)),
      creditLimit: Number(debt.creditLimit || 0),
      available: Math.max(
        0,
        Number(debt.creditLimit || 0) - Math.abs(Number(debt.balance || 0))
      ),
      utilization:
        Number(debt.creditLimit || 0) > 0
          ? Math.round(
              (Math.abs(Number(debt.balance || 0)) /
                Number(debt.creditLimit || 0)) *
                100
            )
          : 0,
      monthlyPayment: parseNumericAmount(debt.maxMonthlyPayment),
      credits: Number(debt.statementCredits || 0),
    }));
  }, [filteredDebts]);

  const balanceBreakdown = useMemo(() => {
    return totalBalanceDebts.map((debt) => ({
      creditor: debt.creditor || "Unknown",
      account: debt.account || "Unknown",
      balance: Math.abs(Number(debt.balance || 0)),
    }));
  }, [totalBalanceDebts]);
  const totalMonthlyPayment = monthlyPaymentsDebts.reduce(
    (sum, debt) => sum + Math.abs(parseNumericAmount(debt.maxMonthlyPayment)),
    0
  );
  const totalOtherLoanBalances = monthlyPaymentsDebts.reduce(
    (sum, debt) => sum + Math.abs(Number(debt.balance || 0)),
    0
  );
  const avgMonthlyPayoffOtherLoans24Months = totalOtherLoanBalances / 24;
  const monthlyPaymentsBreakdown = useMemo(() => {
    return monthlyPaymentsDebts.map((debt) => ({
      creditor: debt.creditor || "Unknown",
      account: debt.account || "Unknown",
      monthlyPayment: Math.abs(parseNumericAmount(debt.maxMonthlyPayment)),
    }));
  }, [monthlyPaymentsDebts]);
  const totalCredits = filteredDebts.reduce(
    (sum, debt) => sum + Number(debt.statementCredits || 0),
    0
  );
  const currentMonthlyRecurringExpensesTotal = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const currentMonth = today.getMonth();
    const currentYear = today.getFullYear();

    return expenses.reduce((sum, expense) => {
      if (!expense.isRecurring) return sum;
      const pattern = (expense.recurrencePattern || "monthly").toLowerCase();
      if (!pattern.includes("month")) return sum;

      const dueDate = new Date(expense.dueDate);
      if (Number.isNaN(dueDate.getTime())) return sum;
      dueDate.setHours(0, 0, 0, 0);
      if (
        dueDate.getMonth() !== currentMonth ||
        dueDate.getFullYear() !== currentYear
      ) {
        return sum;
      }
      if (dueDate.getTime() < today.getTime()) return sum;

      return sum + Math.abs(Number(expense.amount || 0));
    }, 0);
  }, [expenses]);
  const minMonthlyIncomeRequired =
    totalMonthlyPayment +
    avgMonthlyPayoffOtherLoans24Months +
    currentMonthlyRecurringExpensesTotal;

  const debtByCategoryChart = useMemo(() => {
    const totals = new Map<string, number>();
    const monthlyTotals = new Map<string, number>();
    filteredDebts.forEach((debt) => {
      const key = debt.type || "Other";
      const next = (totals.get(key) || 0) + Math.abs(Number(debt.balance || 0));
      totals.set(key, next);
      const nextMonthly =
        (monthlyTotals.get(key) || 0) +
        parseNumericAmount(debt.maxMonthlyPayment);
      monthlyTotals.set(key, nextMonthly);
    });

    const entries = Array.from(totals.entries()).sort((a, b) => b[1] - a[1]);
    return {
      labels: entries.map(([label]) => label),
      values: entries.map(([, value]) => value),
      monthlyValues: entries.map(([label]) => monthlyTotals.get(label) || 0),
    };
  }, [filteredDebts]);

  const canDefaultChart = useMemo(() => {
    let canDefault = 0;
    let cannotDefault = 0;

    filteredDebts.forEach((debt) => {
      const amount = Math.abs(Number(debt.balance || 0));
      if (debt.canDefault) {
        canDefault += amount;
      } else {
        cannotDefault += amount;
      }
    });

    return {
      labels: ["Can Default", "Cannot Default"],
      values: [canDefault, cannotDefault],
    };
  }, [filteredDebts]);

  const debtQualityChart = useMemo(() => {
    let goodDebt = 0;
    let badDebt = 0;

    filteredDebts.forEach((debt) => {
      const amount = Math.abs(Number(debt.balance || 0));
      if (classifyDebtQuality(debt) === "Good Debt") {
        goodDebt += amount;
      } else {
        badDebt += amount;
      }
    });

    return {
      labels: ["Good Debt", "Bad Debt"],
      values: [goodDebt, badDebt],
    };
  }, [filteredDebts]);

  const getPieChartHeight = useCallback((labelCount: number) => {
    const safeCount = Math.max(2, labelCount);
    return Math.max(220, 80 + safeCount * 18);
  }, []);

  const debtByCategoryChartHeight = getPieChartHeight(
    debtByCategoryChart.labels.length
  );
  const canDefaultChartHeight = getPieChartHeight(
    canDefaultChart.labels.length
  );
  const debtQualityChartHeight = getPieChartHeight(
    debtQualityChart.labels.length
  );

  const pieOptions: any = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      layout: {
        padding: {
          top: 8,
          right: 14,
          bottom: 4,
          left: 8,
        },
      },
      onHover: (_event: unknown, elements: Array<{ index: number }>) => {
        document.body.style.cursor = elements.length ? "pointer" : "default";
      },
      plugins: {
        legend: {
          position: "right" as const,
          onHover: (
            _evt: unknown,
            item: { index?: number },
            legend: unknown
          ) => {
            const legendChart = (
              legend as {
                chart: { setActiveElements: Function; update: Function };
              }
            ).chart;
            if (item.index === undefined) return;
            legendChart.setActiveElements([
              { datasetIndex: 0, index: item.index },
            ]);
            legendChart.update();
          },
          onLeave: (_evt: unknown, _item: unknown, legend: unknown) => {
            const legendChart = (
              legend as {
                chart: { setActiveElements: Function; update: Function };
              }
            ).chart;
            legendChart.setActiveElements([]);
            legendChart.update();
          },
          labels: {
            color: "#FFFFFF",
            boxWidth: 12,
            font: { size: 11 },
            generateLabels: (chart: any) => {
              const labels = chart.data.labels || [];
              const dataset = chart.data.datasets[0];
              const values = (dataset?.data || []).map((value: any) =>
                Number(value || 0)
              );
              const total = values.reduce(
                (sum: number, value: number) => sum + value,
                0
              );
              const backgrounds = Array.isArray(dataset?.backgroundColor)
                ? dataset.backgroundColor
                : [];

              return labels.map((label: any, index: number) => {
                const value = values[index] || 0;
                const pct = total > 0 ? (value / total) * 100 : 0;
                return {
                  text: `${String(label)}  ${pct.toFixed(1)}%  ${currencyFormatter.format(
                    value
                  )}`,
                  color: "#FFFFFF",
                  fontColor: "#FFFFFF",
                  fillStyle:
                    typeof backgrounds[index] === "string"
                      ? backgrounds[index]
                      : PIE_COLORS[index % PIE_COLORS.length],
                  strokeStyle:
                    typeof backgrounds[index] === "string"
                      ? backgrounds[index]
                      : PIE_COLORS[index % PIE_COLORS.length],
                  lineWidth: 0,
                  hidden: false,
                  index,
                };
              });
            },
          },
        },
      },
    }),
    []
  );

  const categoryPieOptions: any = useMemo(
    () => ({
      ...pieOptions,
      plugins: {
        ...pieOptions.plugins,
        legend: {
          ...pieOptions.plugins.legend,
          labels: {
            ...pieOptions.plugins.legend.labels,
            generateLabels: (chart: any) => {
              const labels = chart.data.labels || [];
              const dataset = chart.data.datasets[0];
              const values = (dataset?.data || []).map((value: any) =>
                Number(value || 0)
              );
              const total = values.reduce(
                (sum: number, value: number) => sum + value,
                0
              );
              const backgrounds = Array.isArray(dataset?.backgroundColor)
                ? dataset.backgroundColor
                : [];

              return labels.map((label: any, index: number) => {
                const value = values[index] || 0;
                const monthlyValue =
                  debtByCategoryChart.monthlyValues[index] || 0;
                const pct = total > 0 ? (value / total) * 100 : 0;
                return {
                  text: `${String(label)}  ${pct.toFixed(1)}%  ${currencyFormatter.format(
                    value
                  )}  ${currencyFormatter.format(monthlyValue)}/mo`,
                  color: "#FFFFFF",
                  fontColor: "#FFFFFF",
                  fillStyle:
                    typeof backgrounds[index] === "string"
                      ? backgrounds[index]
                      : PIE_COLORS[index % PIE_COLORS.length],
                  strokeStyle:
                    typeof backgrounds[index] === "string"
                      ? backgrounds[index]
                      : PIE_COLORS[index % PIE_COLORS.length],
                  lineWidth: 0,
                  hidden: false,
                  index,
                };
              });
            },
          },
        },
      },
    }),
    [debtByCategoryChart.monthlyValues, pieOptions]
  );

  // Group debts by creditor
  interface CreditorGroup {
    creditor: string;
    debts: Debt[];
    totalBalance: number;
    totalCreditLimit: number;
    totalAvailable: number;
    avgUtilization: number;
    totalPaydown: number;
    totalMonthly: number;
    totalCredits: number;
  }

  const groupedDebts = useMemo(() => {
    const groups = new Map<string, Debt[]>();

    sortedDebts.forEach((debt) => {
      const creditor = debt.creditor || "Unknown";
      if (!groups.has(creditor)) {
        groups.set(creditor, []);
      }
      groups.get(creditor)!.push(debt);
    });

    const result: CreditorGroup[] = [];
    groups.forEach((debts, creditor) => {
      const totalBalance = debts.reduce(
        (sum, d) => sum + Math.abs(Number(d.balance || 0)),
        0
      );
      const totalCreditLimit = debts.reduce(
        (sum, d) => sum + Number(d.creditLimit || 0),
        0
      );
      const totalAvailable = debts.reduce(
        (sum, d) =>
          sum +
          Math.max(
            0,
            Number(d.creditLimit || 0) - Math.abs(Number(d.balance || 0))
          ),
        0
      );
      const debtsWithLimit = debts.filter(
        (d) => Number(d.creditLimit || 0) > 0
      );
      const avgUtilization =
        debtsWithLimit.length > 0
          ? Math.round(
              debtsWithLimit.reduce((sum, d) => {
                const limit = Number(d.creditLimit || 0);
                const balance = Math.abs(Number(d.balance || 0));
                return sum + (balance / limit) * 100;
              }, 0) / debtsWithLimit.length
            )
          : 0;
      const totalPaydown = debts.reduce(
        (sum, d) =>
          sum +
          Math.abs(
            Number(
              d.paydownAmount !== undefined && d.paydownAmount !== null
                ? d.paydownAmount
                : calculateDefaultPaydown(d)
            )
          ),
        0
      );
      const totalMonthly = debts.reduce(
        (sum, d) => sum + parseNumericAmount(d.maxMonthlyPayment),
        0
      );
      const totalCreditsGroup = debts.reduce(
        (sum, d) => sum + Number(d.statementCredits || 0),
        0
      );

      result.push({
        creditor,
        debts,
        totalBalance,
        totalCreditLimit,
        totalAvailable,
        avgUtilization,
        totalPaydown,
        totalMonthly,
        totalCredits: totalCreditsGroup,
      });
    });

    // Sort groups by their aggregate totals based on current sort key
    result.sort((a, b) => {
      let aValue: number;
      let bValue: number;

      // Map sortKey to group property
      switch (sortKey) {
        case "balance":
          aValue = a.totalBalance;
          bValue = b.totalBalance;
          break;
        case "creditLimit":
          aValue = a.totalCreditLimit;
          bValue = b.totalCreditLimit;
          break;
        case "available":
          aValue = a.totalAvailable;
          bValue = b.totalAvailable;
          break;
        case "utilization":
          aValue = a.avgUtilization;
          bValue = b.avgUtilization;
          break;
        case "paydownAmount":
          aValue = a.totalPaydown;
          bValue = b.totalPaydown;
          break;
        case "maxMonthlyPayment":
          aValue = a.totalMonthly;
          bValue = b.totalMonthly;
          break;
        case "statementCredits":
          aValue = a.totalCredits;
          bValue = b.totalCredits;
          break;
        default:
          // For other sort keys (creditor name, type, etc.), sort by totalBalance
          aValue = a.totalBalance;
          bValue = b.totalBalance;
      }

      return sortDirection === "asc" ? aValue - bValue : bValue - aValue;
    });

    return result;
  }, [sortedDebts, calculateDefaultPaydown, sortKey, sortDirection]);

  // Clear all filters
  const clearFilters = () => {
    setStatusFilter("all");
    setTypeFilter("all");
    setSearchQuery("");
    setSharedOnly(false);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };
  const zeroAmountColor = isDarkMode ? "#ffffff" : "#1a202c";
  const getOutflowColor = (
    amount: number | string | null | undefined
  ): string => {
    const normalized = Math.abs(parseNumericAmount(amount));
    return normalized > 0 ? negativeColor : zeroAmountColor;
  };
  const formatOutflow = (amount: number | string | null | undefined) => {
    const normalized = Math.abs(parseNumericAmount(amount));
    return normalized > 0
      ? `-${formatCurrency(normalized)}`
      : formatCurrency(0);
  };
  const formatAlwaysNegative = (amount: number | string | null | undefined) =>
    `-${formatCurrency(Math.abs(parseNumericAmount(amount)))}`;

  // Handle column header click for sorting
  const handleSort = useCallback(
    (key: SortKey) => {
      setSortDirection((prevDirection) => {
        // If clicking the same column, toggle direction
        if (sortKey === key) {
          return prevDirection === "asc" ? "desc" : "asc";
        }
        // If clicking a new column, default to ascending
        return "asc";
      });
      setSortKey(key);
    },
    [sortKey]
  );

  // Render sort indicator
  const renderSortIndicator = (key: SortKey) => {
    if (sortKey !== key) return null;
    return sortDirection === "asc" ? " ↑" : " ↓";
  };

  return (
    <div style={{ marginBottom: "24px", width: "100%" }}>
      {/* Summary Cards */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "repeat(2, 1fr)",
            sm: "repeat(4, 1fr)",
            lg: "repeat(7, 1fr)",
          },
          gap: 2,
          marginBottom: 4,
        }}
      >
        {/* Balance */}
        <Tooltip
          title={
            <Box sx={{ maxHeight: 300, overflow: "auto" }}>
              {balanceBreakdown.map((item, idx) => (
                <Box
                  key={idx}
                  sx={{
                    py: 0.5,
                    borderBottom:
                      idx < balanceBreakdown.length - 1
                        ? "1px solid rgba(255,255,255,0.1)"
                        : "none",
                  }}
                >
                  <Typography sx={{ fontSize: "11px", fontWeight: "bold" }}>
                    {item.creditor}
                  </Typography>
                  <Typography sx={{ fontSize: "10px", pl: 1 }}>
                    {item.account}: {formatCurrency(item.balance)}
                  </Typography>
                </Box>
              ))}
            </Box>
          }
          arrow
          placement="bottom"
        >
          <Card
            sx={{
              borderTop: "4px solid #e53e3e",
              height: "100%",
              backgroundColor: isDarkMode ? "#1a1a1a" : "white",
              boxShadow: isDarkMode
                ? "0 4px 6px rgba(0, 0, 0, 0.3)"
                : "0 1px 2px rgba(0,0,0,0.05)",
              cursor: "pointer",
            }}
          >
            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                }}
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  gutterBottom
                  sx={{ fontSize: "12px" }}
                >
                  Balance
                </Typography>
                <Tooltip
                  title="Exclude debt types from this Balance card"
                  arrow
                >
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setTotalTypesAnchorEl(e.currentTarget);
                    }}
                    sx={{
                      p: 0.25,
                      color: isDarkMode
                        ? "rgba(255,255,255,0.7)"
                        : "rgba(0,0,0,0.6)",
                    }}
                  >
                    <FilterListIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: "bold",
                  fontSize: "16px",
                  color: getOutflowColor(totalBalanceCard),
                  display: "flex",
                  alignItems: "baseline",
                  gap: 0.75,
                }}
              >
                {renderTotalsValue(formatOutflow(totalBalanceCard), 90)}
                <Typography
                  component="span"
                  sx={{
                    fontSize: "11px",
                    fontWeight: 500,
                    color: "text.secondary",
                    opacity: 0.9,
                  }}
                >
                  {" • "}
                  {renderTotalsValue(
                    `${formatCurrency(totalMonthlyPayment)}/mo`,
                    70
                  )}
                </Typography>
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mt: 0.25,
                  color: "text.secondary",
                  opacity: 0.8,
                  fontSize: "11px",
                }}
              >
                Unfiltered:{" "}
                {renderTotalsValue(formatOutflow(totalBalanceUnfiltered), 90)}
              </Typography>
            </CardContent>
          </Card>
        </Tooltip>
        {/* Limit */}
        <Tooltip
          title={
            <Box sx={{ maxHeight: 300, overflow: "auto" }}>
              {allDebtsBreakdown
                .filter((item) => item.creditLimit > 0)
                .map((item, idx, arr) => (
                  <Box
                    key={idx}
                    sx={{
                      py: 0.5,
                      borderBottom:
                        idx < arr.length - 1
                          ? "1px solid rgba(255,255,255,0.1)"
                          : "none",
                    }}
                  >
                    <Typography sx={{ fontSize: "11px", fontWeight: "bold" }}>
                      {item.creditor}
                    </Typography>
                    <Typography sx={{ fontSize: "10px", pl: 1 }}>
                      {item.account}: {formatCurrency(item.creditLimit)}
                    </Typography>
                  </Box>
                ))}
            </Box>
          }
          arrow
          placement="bottom"
        >
          <Card
            sx={{
              borderTop: "4px solid #38a169",
              height: "100%",
              backgroundColor: isDarkMode ? "#1a1a1a" : "white",
              boxShadow: isDarkMode
                ? "0 4px 6px rgba(0, 0, 0, 0.3)"
                : "0 1px 2px rgba(0,0,0,0.05)",
              cursor: "pointer",
            }}
          >
            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
              <Typography
                variant="body2"
                color="text.secondary"
                gutterBottom
                sx={{ fontSize: "12px" }}
              >
                Credit Limit
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: "bold",
                  fontSize: "16px",
                  color: positiveColor,
                }}
              >
                {renderTotalsValue(formatCurrency(totalCreditLimit), 80)}
              </Typography>
            </CardContent>
          </Card>
        </Tooltip>
        {/* Available Credit */}
        <Tooltip
          title={
            <Box sx={{ maxHeight: 300, overflow: "auto" }}>
              {allDebtsBreakdown
                .filter((item) => item.creditLimit > 0)
                .map((item, idx, arr) => (
                  <Box
                    key={idx}
                    sx={{
                      py: 0.5,
                      borderBottom:
                        idx < arr.length - 1
                          ? "1px solid rgba(255,255,255,0.1)"
                          : "none",
                    }}
                  >
                    <Typography sx={{ fontSize: "11px", fontWeight: "bold" }}>
                      {item.creditor}
                    </Typography>
                    <Typography sx={{ fontSize: "10px", pl: 1 }}>
                      {item.account}: {formatCurrency(item.available)}
                    </Typography>
                  </Box>
                ))}
            </Box>
          }
          arrow
          placement="bottom"
        >
          <Card
            sx={{
              borderTop: "4px solid #38a169",
              height: "100%",
              backgroundColor: isDarkMode ? "#1a1a1a" : "white",
              boxShadow: isDarkMode
                ? "0 4px 6px rgba(0, 0, 0, 0.3)"
                : "0 1px 2px rgba(0,0,0,0.05)",
              cursor: "pointer",
            }}
          >
            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
              <Typography
                variant="body2"
                color="text.secondary"
                gutterBottom
                sx={{ fontSize: "12px" }}
              >
                Available Credit
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: "bold",
                  fontSize: "16px",
                  color: positiveColor,
                }}
              >
                {renderTotalsValue(formatCurrency(totalAvailable), 80)}
              </Typography>
            </CardContent>
          </Card>
        </Tooltip>
        {/* Credit Utilization */}
        <Tooltip
          title={
            <Box sx={{ maxHeight: 300, overflow: "auto" }}>
              {allDebtsBreakdown
                .filter((item) => item.creditLimit > 0)
                .map((item, idx, arr) => (
                  <Box
                    key={idx}
                    sx={{
                      py: 0.5,
                      borderBottom:
                        idx < arr.length - 1
                          ? "1px solid rgba(255,255,255,0.1)"
                          : "none",
                    }}
                  >
                    <Typography sx={{ fontSize: "11px", fontWeight: "bold" }}>
                      {item.creditor}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "10px",
                        pl: 1,
                        color: item.utilization > 30 ? "#ff6b6b" : "#69db7c",
                      }}
                    >
                      {item.account}: {item.utilization}%
                    </Typography>
                  </Box>
                ))}
            </Box>
          }
          arrow
          placement="bottom"
        >
          <Card
            sx={{
              borderTop: `4px solid ${
                useColors
                  ? averageUtilization > 30
                    ? "#e53e3e"
                    : "#38a169"
                  : isDarkMode
                    ? "#555"
                    : "#ccc"
              }`,
              height: "100%",
              backgroundColor: isDarkMode ? "#1a1a1a" : "white",
              boxShadow: isDarkMode
                ? "0 4px 6px rgba(0, 0, 0, 0.3)"
                : "0 1px 2px rgba(0,0,0,0.05)",
              cursor: "pointer",
            }}
          >
            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
              <Typography
                variant="body2"
                color="text.secondary"
                gutterBottom
                sx={{ fontSize: "12px" }}
              >
                Credit Utilization
              </Typography>
              <Box sx={{ mb: 1 }}>
                <Box
                  sx={{
                    width: "100%",
                    height: 10,
                    borderRadius: 999,
                    overflow: "hidden",
                    display: "flex",
                    border: "1px solid",
                    borderColor: isDarkMode
                      ? "rgba(255,255,255,0.15)"
                      : "rgba(0,0,0,0.12)",
                  }}
                >
                  <Box
                    sx={{
                      width: `${utilizationUsedPct}%`,
                      backgroundColor: "#e53e3e",
                      transition: "width 200ms ease",
                    }}
                  />
                  <Box
                    sx={{
                      width: `${utilizationAvailablePct}%`,
                      backgroundColor: "#38a169",
                      transition: "width 200ms ease",
                    }}
                  />
                </Box>
                <Box
                  sx={{
                    mt: 0.5,
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 1,
                  }}
                >
                  <Typography
                    sx={{ fontSize: "10px", color: "#e53e3e", fontWeight: 600 }}
                  >
                    Used {formatCurrency(totalUsedCredit)}
                  </Typography>
                  <Typography
                    sx={{ fontSize: "10px", color: "#38a169", fontWeight: 600 }}
                  >
                    Available {formatCurrency(totalAvailable)}
                  </Typography>
                </Box>
              </Box>
              {renderTotalsValue(
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <Box sx={{ textAlign: "center" }}>
                    <Typography
                      sx={{
                        fontSize: "10px",
                        color: "text.secondary",
                      }}
                    >
                      Low
                    </Typography>
                    <Typography
                      sx={{
                        fontWeight: "bold",
                        fontSize: "14px",
                        color: useColors
                          ? lowestUtilization > 30
                            ? "#e53e3e"
                            : "#38a169"
                          : "inherit",
                      }}
                    >
                      {lowestUtilization}%
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: "center" }}>
                    <Typography
                      sx={{
                        fontSize: "10px",
                        color: "text.secondary",
                      }}
                    >
                      Avg
                    </Typography>
                    <Typography
                      sx={{
                        fontWeight: "bold",
                        fontSize: "16px",
                        color: useColors
                          ? averageUtilization > 30
                            ? "#e53e3e"
                            : "#38a169"
                          : "inherit",
                      }}
                    >
                      {averageUtilization}%
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: "center" }}>
                    <Typography
                      sx={{
                        fontSize: "10px",
                        color: "text.secondary",
                      }}
                    >
                      High
                    </Typography>
                    <Typography
                      sx={{
                        fontWeight: "bold",
                        fontSize: "14px",
                        color: useColors
                          ? highestUtilization > 30
                            ? "#e53e3e"
                            : "#38a169"
                          : "inherit",
                      }}
                    >
                      {highestUtilization}%
                    </Typography>
                  </Box>
                </Box>,
                120
              )}
            </CardContent>
          </Card>
        </Tooltip>
        {/* Paydown */}
        <Tooltip
          title={
            <Box sx={{ maxHeight: 300, overflow: "auto" }}>
              <Typography sx={{ fontSize: "11px", fontWeight: "bold", mb: 1 }}>
                Amount to reach 33% utilization:
              </Typography>
              {creditAffectingBreakdown.length === 0 ? (
                <Typography sx={{ fontSize: "10px", fontStyle: "italic" }}>
                  No credit-affecting items
                </Typography>
              ) : (
                creditAffectingBreakdown.map((item, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      py: 0.5,
                      borderBottom:
                        idx < creditAffectingBreakdown.length - 1
                          ? "1px solid rgba(255,255,255,0.1)"
                          : "none",
                    }}
                  >
                    <Typography sx={{ fontSize: "11px", fontWeight: "bold" }}>
                      {item.creditor}
                    </Typography>
                    <Typography sx={{ fontSize: "10px", pl: 1 }}>
                      {item.account}
                    </Typography>
                    <Typography sx={{ fontSize: "10px", pl: 2 }}>
                      Balance: {formatCurrency(item.balance)} → Paydown:{" "}
                      {formatCurrency(item.paydown)}
                    </Typography>
                  </Box>
                ))
              )}
            </Box>
          }
          arrow
          placement="bottom"
        >
          <Card
            sx={{
              borderTop: "4px solid #dd6b20",
              height: "100%",
              backgroundColor: isDarkMode ? "#1a1a1a" : "white",
              boxShadow: isDarkMode
                ? "0 4px 6px rgba(0, 0, 0, 0.3)"
                : "0 1px 2px rgba(0,0,0,0.05)",
              cursor: "pointer",
            }}
          >
            <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: "12px", lineHeight: 1.2, mb: 0.5 }}
              >
                Paydown
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    sx={{ fontSize: "10px", color: "text.secondary" }}
                  >
                    All
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "12px",
                      fontWeight: "bold",
                      color: negativeColor,
                    }}
                  >
                    {renderTotalsValue(
                      formatAlwaysNegative(allDebtsPaydownAmount),
                      70
                    )}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    sx={{ fontSize: "10px", color: "text.secondary" }}
                  >
                    Credit Cards
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "12px",
                      fontWeight: "bold",
                      color: negativeColor,
                    }}
                  >
                    {renderTotalsValue(
                      formatAlwaysNegative(creditCardsOnlyPaydownAmount),
                      70
                    )}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    sx={{ fontSize: "10px", color: "text.secondary" }}
                  >
                    Affects Credit Score
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "12px",
                      fontWeight: "bold",
                      color: negativeColor,
                    }}
                  >
                    {renderTotalsValue(
                      formatAlwaysNegative(creditAffectingPaydownAmount),
                      70
                    )}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Tooltip>
        {/* Monthly Payments */}
        <Tooltip
          title={
            <Box sx={{ maxHeight: 300, overflow: "auto" }}>
              {monthlyPaymentsBreakdown
                .filter((item) => item.monthlyPayment > 0)
                .map((item, idx, arr) => (
                  <Box
                    key={idx}
                    sx={{
                      py: 0.5,
                      borderBottom:
                        idx < arr.length - 1
                          ? "1px solid rgba(255,255,255,0.1)"
                          : "none",
                    }}
                  >
                    <Typography sx={{ fontSize: "11px", fontWeight: "bold" }}>
                      {item.creditor}
                    </Typography>
                    <Typography sx={{ fontSize: "10px", pl: 1 }}>
                      {item.account}: {formatCurrency(item.monthlyPayment)}
                    </Typography>
                  </Box>
                ))}
              {monthlyPaymentsBreakdown.filter(
                (item) => item.monthlyPayment > 0
              ).length === 0 && (
                <Typography sx={{ fontSize: "10px", fontStyle: "italic" }}>
                  No monthly payments set
                </Typography>
              )}
            </Box>
          }
          arrow
          placement="bottom"
        >
          <Card
            sx={{
              borderTop: "4px solid #e53e3e",
              height: "100%",
              backgroundColor: isDarkMode ? "#1a1a1a" : "white",
              boxShadow: isDarkMode
                ? "0 4px 6px rgba(0, 0, 0, 0.3)"
                : "0 1px 2px rgba(0,0,0,0.05)",
              cursor: "pointer",
            }}
          >
            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                }}
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  gutterBottom
                  sx={{ fontSize: "12px" }}
                >
                  Monthly Payments
                </Typography>
                <Tooltip
                  title="Exclude debt types from this Monthly Payments card"
                  arrow
                >
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setMonthlyTypesAnchorEl(e.currentTarget);
                    }}
                    sx={{
                      p: 0.25,
                      color: isDarkMode
                        ? "rgba(255,255,255,0.7)"
                        : "rgba(0,0,0,0.6)",
                    }}
                  >
                    <FilterListIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              </Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: "bold",
                  fontSize: "16px",
                  color: negativeColor,
                }}
              >
                {renderTotalsValue(
                  formatAlwaysNegative(totalMonthlyPayment),
                  90
                )}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mt: 0.25,
                  color: "text.secondary",
                  opacity: 0.8,
                  fontSize: "11px",
                }}
              >
                <Tooltip
                  title="This is the average monthly payments to payoff all other loan balances if averaged over in 24 months."
                  arrow
                >
                  <span>Flex Payments</span>
                </Tooltip>
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontSize: "12px", fontWeight: 700, color: negativeColor }}
              >
                {renderTotalsValue(
                  formatAlwaysNegative(avgMonthlyPayoffOtherLoans24Months),
                  90
                )}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  mt: 0.5,
                  color: "text.secondary",
                  opacity: 0.8,
                  fontSize: "11px",
                }}
              >
                <span
                  role="button"
                  tabIndex={0}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setMinIncomeBreakdownOpen(true);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setMinIncomeBreakdownOpen(true);
                    }
                  }}
                  style={{
                    textDecoration: "underline",
                    cursor: "pointer",
                    color: "inherit",
                  }}
                >
                  Min. Monthly Income Required:
                </span>
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontSize: "12px", fontWeight: 700, color: negativeColor }}
              >
                {renderTotalsValue(
                  formatAlwaysNegative(minMonthlyIncomeRequired),
                  90
                )}
              </Typography>
            </CardContent>
          </Card>
        </Tooltip>
        {/* Credits */}
        <Tooltip
          title={
            <Box sx={{ maxHeight: 300, overflow: "auto" }}>
              {allDebtsBreakdown
                .filter((item) => item.credits > 0)
                .map((item, idx, arr) => (
                  <Box
                    key={idx}
                    sx={{
                      py: 0.5,
                      borderBottom:
                        idx < arr.length - 1
                          ? "1px solid rgba(255,255,255,0.1)"
                          : "none",
                    }}
                  >
                    <Typography sx={{ fontSize: "11px", fontWeight: "bold" }}>
                      {item.creditor}
                    </Typography>
                    <Typography sx={{ fontSize: "10px", pl: 1 }}>
                      {item.account}: {formatCurrency(item.credits)}
                    </Typography>
                  </Box>
                ))}
              {allDebtsBreakdown.filter((item) => item.credits > 0).length ===
                0 && (
                <Typography sx={{ fontSize: "10px", fontStyle: "italic" }}>
                  No credits set
                </Typography>
              )}
            </Box>
          }
          arrow
          placement="bottom"
        >
          <Card
            sx={{
              borderTop: "4px solid #319795",
              height: "100%",
              backgroundColor: isDarkMode ? "#1a1a1a" : "white",
              boxShadow: isDarkMode
                ? "0 4px 6px rgba(0, 0, 0, 0.3)"
                : "0 1px 2px rgba(0,0,0,0.05)",
              cursor: "pointer",
            }}
          >
            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
              <Typography
                variant="body2"
                color="text.secondary"
                gutterBottom
                sx={{ fontSize: "12px" }}
              >
                Credits
              </Typography>
              <Typography
                variant="h6"
                sx={{ fontWeight: "bold", fontSize: "16px" }}
              >
                {renderTotalsValue(formatCurrency(totalCredits), 70)}
              </Typography>
            </CardContent>
          </Card>
        </Tooltip>
      </Box>

      {/* Debt Pie Charts */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr", lg: "1fr 1fr 1fr" },
          gap: 2,
          mb: 3,
        }}
      >
        <Card
          sx={{
            backgroundColor: isDarkMode ? "#1a1a1a" : "white",
            boxShadow: isDarkMode
              ? "0 4px 6px rgba(0, 0, 0, 0.3)"
              : "0 1px 2px rgba(0,0,0,0.05)",
          }}
        >
          <CardContent sx={{ p: 2 }}>
            <Box
              sx={{
                mb: 1,
                display: "flex",
                alignItems: "baseline",
                justifyContent: "space-between",
                gap: 1,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Debt Balance by Category
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: "text.secondary", opacity: 0.9 }}
              >
                {formatCurrency(totalMonthlyPayment)}/mo
              </Typography>
            </Box>
            <Box sx={{ height: debtByCategoryChartHeight }}>
              <Pie
                data={{
                  labels: debtByCategoryChart.labels,
                  datasets: [
                    {
                      data: debtByCategoryChart.values,
                      backgroundColor: debtByCategoryChart.values.map(
                        (_, idx) => PIE_COLORS[idx % PIE_COLORS.length]
                      ),
                      borderWidth: 1,
                    },
                  ],
                }}
                options={categoryPieOptions}
                plugins={[pieCalloutPlugin]}
              />
            </Box>
          </CardContent>
        </Card>

        <Card
          sx={{
            backgroundColor: isDarkMode ? "#1a1a1a" : "white",
            boxShadow: isDarkMode
              ? "0 4px 6px rgba(0, 0, 0, 0.3)"
              : "0 1px 2px rgba(0,0,0,0.05)",
          }}
        >
          <CardContent sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Debt Balance by Can Default
            </Typography>
            <Box sx={{ height: canDefaultChartHeight }}>
              <Pie
                data={{
                  labels: canDefaultChart.labels,
                  datasets: [
                    {
                      data: canDefaultChart.values,
                      backgroundColor: ["#DD6B20", "#3182CE"],
                      hoverBackgroundColor: ["#F59E0B", "#3B82F6"],
                      borderColor: ["#9A3412", "#1D4ED8"],
                      borderWidth: 1.25,
                    },
                  ],
                }}
                options={{
                  ...pieOptions,
                  plugins: {
                    ...pieOptions.plugins,
                    legend: {
                      ...pieOptions.plugins.legend,
                      labels: {
                        ...pieOptions.plugins.legend.labels,
                        color: "#FFFFFF",
                      },
                    },
                  },
                }}
                plugins={[pieCalloutPlugin]}
              />
            </Box>
          </CardContent>
        </Card>

        <Card
          sx={{
            backgroundColor: isDarkMode ? "#1a1a1a" : "white",
            boxShadow: isDarkMode
              ? "0 4px 6px rgba(0, 0, 0, 0.3)"
              : "0 1px 2px rgba(0,0,0,0.05)",
          }}
        >
          <CardContent sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Debt Balance by Good vs Bad
            </Typography>
            <Box sx={{ height: debtQualityChartHeight }}>
              <Pie
                data={{
                  labels: debtQualityChart.labels,
                  datasets: [
                    {
                      data: debtQualityChart.values,
                      backgroundColor: ["#16A34A", "#DC2626"],
                      hoverBackgroundColor: ["#22C55E", "#EF4444"],
                      borderColor: ["#15803D", "#B91C1C"],
                      borderWidth: 1.25,
                    },
                  ],
                }}
                options={pieOptions}
                plugins={[pieCalloutPlugin]}
              />
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Filter Bar */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          mb: 3,
          p: 2,
          backgroundColor: isDarkMode ? "#1a1a1a" : "white",
          borderRadius: "8px",
          boxShadow: isDarkMode
            ? "0 4px 6px rgba(0, 0, 0, 0.3)"
            : "0 1px 3px rgba(0,0,0,0.1)",
          alignItems: "center",
        }}
      >
        <FilterListIcon
          sx={{ color: isDarkMode ? "rgba(255,255,255,0.7)" : "#4a5568" }}
        />

        {/* Search */}
        <TextField
          size="small"
          placeholder="Search accounts..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ minWidth: 200 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        {/* Status Filter */}
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            label="Status"
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <MenuItem value="all">All Statuses</MenuItem>
            {uniqueStatuses.map((status) => (
              <MenuItem key={status} value={status}>
                {status}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Type Filter */}
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Type</InputLabel>
          <Select
            value={typeFilter}
            label="Type"
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            <MenuItem value="all">All Types</MenuItem>
            {uniqueTypes.map((type) => (
              <MenuItem key={type} value={type}>
                {type}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Group by Creditor Toggle */}
        <Tooltip
          title={groupByCreditor ? "Disable grouping" : "Group by creditor"}
        >
          <Chip
            icon={<GroupWorkIcon sx={{ fontSize: 18 }} />}
            label="Group by Creditor"
            onClick={() => setGroupByCreditor(!groupByCreditor)}
            color={groupByCreditor ? "primary" : "default"}
            variant={groupByCreditor ? "filled" : "outlined"}
            sx={{
              cursor: "pointer",
              fontWeight: groupByCreditor ? 600 : 400,
            }}
          />
        </Tooltip>

        {/* Color Toggle */}
        <Tooltip title={useColors ? "Disable colors" : "Enable colors"}>
          <Chip
            icon={<PaletteIcon sx={{ fontSize: 18 }} />}
            label="Colors"
            onClick={() => setUseColors(!useColors)}
            color={useColors ? "primary" : "default"}
            variant={useColors ? "filled" : "outlined"}
            sx={{
              cursor: "pointer",
              fontWeight: useColors ? 600 : 400,
            }}
          />
        </Tooltip>

        {/* Shared Filter Toggle */}
        <Tooltip title={sharedOnly ? "Show all debts" : "Show shared only"}>
          <Chip
            label="Shared Only"
            onClick={() => setSharedOnly((prev) => !prev)}
            color={sharedOnly ? "primary" : "default"}
            variant={sharedOnly ? "filled" : "outlined"}
            sx={{
              cursor: "pointer",
              fontWeight: sharedOnly ? 600 : 400,
            }}
          />
        </Tooltip>

        {/* Column Visibility Toggle */}
        <Tooltip title="Show/Hide Columns">
          <Chip
            icon={<ViewColumnIcon sx={{ fontSize: 18 }} />}
            label="Columns"
            onClick={(e) => setColumnsAnchorEl(e.currentTarget)}
            color={
              visibleColumns.size < COLUMN_DEFINITIONS.length
                ? "primary"
                : "default"
            }
            variant={
              visibleColumns.size < COLUMN_DEFINITIONS.length
                ? "filled"
                : "outlined"
            }
            sx={{
              cursor: "pointer",
              fontWeight:
                visibleColumns.size < COLUMN_DEFINITIONS.length ? 600 : 400,
            }}
          />
        </Tooltip>

        {/* Columns Popover */}
        <Popover
          open={columnsPopoverOpen}
          anchorEl={columnsAnchorEl}
          onClose={() => setColumnsAnchorEl(null)}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
        >
          <Box sx={{ p: 2, minWidth: 200 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Visible Columns
            </Typography>
            <FormGroup>
              {COLUMN_DEFINITIONS.map((col) => (
                <FormControlLabel
                  key={col.key}
                  control={
                    <Checkbox
                      size="small"
                      checked={isColumnVisible(col.key)}
                      onChange={() => toggleColumnVisibility(col.key)}
                    />
                  }
                  label={col.label}
                  sx={{ "& .MuiFormControlLabel-label": { fontSize: "13px" } }}
                />
              ))}
            </FormGroup>
            <Box sx={{ mt: 1, display: "flex", gap: 1 }}>
              <Button
                size="small"
                onClick={() => {
                  setVisibleColumns(
                    new Set(COLUMN_DEFINITIONS.map((c) => c.key))
                  );
                  localStorage.setItem(
                    COLUMN_VISIBILITY_STORAGE_KEY,
                    JSON.stringify(COLUMN_DEFINITIONS.map((c) => c.key))
                  );
                }}
              >
                Show All
              </Button>
              <Button
                size="small"
                onClick={() => {
                  setVisibleColumns(new Set(DEFAULT_VISIBLE_COLUMNS));
                  localStorage.setItem(
                    COLUMN_VISIBILITY_STORAGE_KEY,
                    JSON.stringify(DEFAULT_VISIBLE_COLUMNS)
                  );
                }}
              >
                Reset
              </Button>
            </Box>
          </Box>
        </Popover>

        {/* Monthly Payments Card Types Popover */}
        <Popover
          open={monthlyTypesPopoverOpen}
          anchorEl={monthlyTypesAnchorEl}
          onClose={() => setMonthlyTypesAnchorEl(null)}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
        >
          <Box sx={{ p: 2, minWidth: 320, maxWidth: 420 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Exclude Types From Monthly Payments Card
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mb: 1 }}
            >
              Type to search. Selected types are excluded from the Monthly
              Payments total.
            </Typography>
            <Autocomplete
              multiple
              options={uniqueTypes}
              value={monthlyPaymentsExcludedTypes}
              onChange={(_, value) => updateMonthlyPaymentsExcludedTypes(value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Excluded Types"
                  placeholder='Ex. "Mortgage", "Auto Loan", "Taxes", "Revolving"'
                  size="small"
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    variant="outlined"
                    size="small"
                    label={option}
                    {...getTagProps({ index })}
                    key={`${option}-${index}`}
                  />
                ))
              }
              sx={{ mt: 1 }}
            />
            <Box sx={{ mt: 1, display: "flex", gap: 1 }}>
              <Button
                size="small"
                onClick={() => updateMonthlyPaymentsExcludedTypes([])}
              >
                Include All
              </Button>
              <Button
                size="small"
                onClick={() =>
                  updateMonthlyPaymentsExcludedTypes(
                    DEFAULT_MONTHLY_PAYMENTS_EXCLUDED_TYPES
                  )
                }
              >
                Reset Defaults
              </Button>
            </Box>
          </Box>
        </Popover>

        {/* Total Balance Card Types Popover */}
        <Popover
          open={totalTypesPopoverOpen}
          anchorEl={totalTypesAnchorEl}
          onClose={() => setTotalTypesAnchorEl(null)}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
        >
          <Box sx={{ p: 2, minWidth: 320, maxWidth: 420 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Exclude Types From Balance Card
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mb: 1 }}
            >
              Type to search. Selected types are excluded from the Balance card
              total.
            </Typography>
            <Autocomplete
              multiple
              options={uniqueTypes}
              value={totalExcludedTypes}
              onChange={(_, value) => updateTotalExcludedTypes(value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Excluded Types"
                  placeholder='Ex. "Auto Loan", "Mortgage"'
                  size="small"
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    variant="outlined"
                    size="small"
                    label={option}
                    {...getTagProps({ index })}
                    key={`${option}-${index}`}
                  />
                ))
              }
              sx={{ mt: 1 }}
            />
            <Box sx={{ mt: 1, display: "flex", gap: 1 }}>
              <Button size="small" onClick={() => updateTotalExcludedTypes([])}>
                Include All
              </Button>
              <Button
                size="small"
                onClick={() =>
                  updateTotalExcludedTypes(DEFAULT_TOTAL_BALANCE_EXCLUDED_TYPES)
                }
              >
                Reset Defaults
              </Button>
            </Box>
          </Box>
        </Popover>

        {/* Active Filters Display & Clear */}
        {(statusFilter !== "all" || typeFilter !== "all" || searchQuery) && (
          <Box sx={{ display: "flex", gap: 1, alignItems: "center", ml: 1 }}>
            {statusFilter !== "all" && (
              <Chip
                label={`Status: ${statusFilter}`}
                size="small"
                onDelete={() => setStatusFilter("all")}
                color="primary"
                variant="outlined"
              />
            )}
            {typeFilter !== "all" && (
              <Chip
                label={`Type: ${typeFilter}`}
                size="small"
                onDelete={() => setTypeFilter("all")}
                color="primary"
                variant="outlined"
              />
            )}
            {searchQuery && (
              <Chip
                label={`Search: "${searchQuery}"`}
                size="small"
                onDelete={() => setSearchQuery("")}
                color="primary"
                variant="outlined"
              />
            )}
            {sharedOnly && (
              <Chip
                label="Shared Only"
                size="small"
                onDelete={() => setSharedOnly(false)}
                color="primary"
                variant="outlined"
              />
            )}
            <Chip
              label="Clear All"
              size="small"
              onClick={clearFilters}
              color="error"
              variant="outlined"
              sx={{ cursor: "pointer" }}
            />
          </Box>
        )}

        {/* Results count */}
        <Box sx={{ ml: "auto" }}>
          <Chip
            label={`${filteredDebts.length} of ${debts.length} debts`}
            size="small"
            variant="filled"
            sx={{
              backgroundColor: isDarkMode ? "#2d2d2d" : "#e2e8f0",
              color: isDarkMode ? "rgba(255,255,255,0.9)" : "#4a5568",
            }}
          />
        </Box>
      </Box>

      {/* Table */}
      <div
        style={{
          backgroundColor: isDarkMode ? "#1a1a1a" : "white",
          borderRadius: "8px",
          boxShadow: isDarkMode
            ? "0 4px 6px rgba(0, 0, 0, 0.3)"
            : "0 1px 3px rgba(0,0,0,0.1)",
          overflowX: "auto",
          width: "100%",
        }}
      >
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            tableLayout: "fixed",
            minWidth: "1600px",
            fontSize: "12px",
          }}
        >
          <thead>
            <tr
              style={{
                backgroundColor: isDarkMode ? "#2d2d2d" : "#f7fafc",
                textAlign: "left",
              }}
            >
              {isColumnVisible("status") && (
                <th
                  style={{
                    padding: "6px 4px",
                    borderBottom: isDarkMode
                      ? "1px solid rgba(255,255,255,0.1)"
                      : "1px solid #e2e8f0",
                    color: isDarkMode ? "rgba(255,255,255,0.9)" : "inherit",
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                    width: "5%",
                  }}
                  onClick={() => handleSort("status")}
                >
                  <Tooltip title="Status" arrow>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <CheckCircleOutlineIcon fontSize="small" />
                      {renderSortIndicator("status")}
                    </Box>
                  </Tooltip>
                </th>
              )}
              {isColumnVisible("type") && (
                <th
                  style={{
                    padding: "6px 4px",
                    borderBottom: isDarkMode
                      ? "1px solid rgba(255,255,255,0.1)"
                      : "1px solid #e2e8f0",
                    color: isDarkMode ? "rgba(255,255,255,0.9)" : "inherit",
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                    width: "5%",
                  }}
                  onClick={() => handleSort("type")}
                >
                  <Tooltip title="Type" arrow>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <CategoryIcon fontSize="small" />
                      {renderSortIndicator("type")}
                    </Box>
                  </Tooltip>
                </th>
              )}
              {isColumnVisible("quality") && (
                <th
                  style={{
                    padding: "6px 4px",
                    borderBottom: isDarkMode
                      ? "1px solid rgba(255,255,255,0.1)"
                      : "1px solid #e2e8f0",
                    color: isDarkMode ? "rgba(255,255,255,0.9)" : "inherit",
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                    width: "5%",
                  }}
                  onClick={() => handleSort("debtQuality")}
                >
                  <Tooltip title="Quality" arrow>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <GradeIcon fontSize="small" />
                      {renderSortIndicator("debtQuality")}
                    </Box>
                  </Tooltip>
                </th>
              )}
              {isColumnVisible("canDefault") && (
                <th
                  style={{
                    padding: "6px 4px",
                    borderBottom: isDarkMode
                      ? "1px solid rgba(255,255,255,0.1)"
                      : "1px solid #e2e8f0",
                    color: isDarkMode ? "rgba(255,255,255,0.9)" : "inherit",
                    whiteSpace: "nowrap",
                    width: "6%",
                  }}
                >
                  <Tooltip title="Can Default" arrow>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <BlockIcon fontSize="small" />
                    </Box>
                  </Tooltip>
                </th>
              )}
              {isColumnVisible("creditor") && (
                <th
                  style={{
                    padding: "6px 4px",
                    borderBottom: isDarkMode
                      ? "1px solid rgba(255,255,255,0.1)"
                      : "1px solid #e2e8f0",
                    color: isDarkMode ? "rgba(255,255,255,0.9)" : "inherit",
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                    width: "10%",
                  }}
                  onClick={() => handleSort("creditor")}
                >
                  <Tooltip title="Creditor" arrow>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <BusinessIcon fontSize="small" />
                      {renderSortIndicator("creditor")}
                    </Box>
                  </Tooltip>
                </th>
              )}
              {isColumnVisible("account") && (
                <th
                  style={{
                    padding: "6px 4px",
                    borderBottom: isDarkMode
                      ? "1px solid rgba(255,255,255,0.1)"
                      : "1px solid #e2e8f0",
                    color: isDarkMode ? "rgba(255,255,255,0.9)" : "inherit",
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                    width: "8%",
                  }}
                  onClick={() => handleSort("account")}
                >
                  <Tooltip title="Account" arrow>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <CreditCardIcon fontSize="small" />
                      {renderSortIndicator("account")}
                    </Box>
                  </Tooltip>
                </th>
              )}
              {isColumnVisible("balance") && (
                <th
                  style={{
                    padding: "6px 4px",
                    borderBottom: isDarkMode
                      ? "1px solid rgba(255,255,255,0.1)"
                      : "1px solid #e2e8f0",
                    color: isDarkMode ? "rgba(255,255,255,0.9)" : "inherit",
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                    width: "7%",
                  }}
                  onClick={() => handleSort("balance")}
                >
                  <Tooltip title="Balance" arrow>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <AccountBalanceWalletIcon fontSize="small" />
                      {renderSortIndicator("balance")}
                    </Box>
                  </Tooltip>
                </th>
              )}
              {isColumnVisible("limit") && (
                <th
                  style={{
                    padding: "6px 4px",
                    borderBottom: isDarkMode
                      ? "1px solid rgba(255,255,255,0.1)"
                      : "1px solid #e2e8f0",
                    color: isDarkMode ? "rgba(255,255,255,0.9)" : "inherit",
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                    width: "6%",
                  }}
                  onClick={() => handleSort("creditLimit")}
                >
                  <Tooltip title="Limit" arrow>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <SpeedIcon fontSize="small" />
                      {renderSortIndicator("creditLimit")}
                    </Box>
                  </Tooltip>
                </th>
              )}
              {isColumnVisible("available") && (
                <th
                  style={{
                    padding: "6px 4px",
                    borderBottom: isDarkMode
                      ? "1px solid rgba(255,255,255,0.1)"
                      : "1px solid #e2e8f0",
                    color: isDarkMode ? "rgba(255,255,255,0.9)" : "inherit",
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                    width: "6%",
                  }}
                  onClick={() => handleSort("available")}
                >
                  <Tooltip title="Available" arrow>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <SavingsIcon fontSize="small" />
                      {renderSortIndicator("available")}
                    </Box>
                  </Tooltip>
                </th>
              )}
              {isColumnVisible("utilization") && (
                <th
                  style={{
                    padding: "6px 4px",
                    borderBottom: isDarkMode
                      ? "1px solid rgba(255,255,255,0.1)"
                      : "1px solid #e2e8f0",
                    color: isDarkMode ? "rgba(255,255,255,0.9)" : "inherit",
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                    width: "7%",
                  }}
                  onClick={() => handleSort("utilization")}
                >
                  <Tooltip title="Utilization" arrow>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <DonutLargeIcon fontSize="small" />
                      {renderSortIndicator("utilization")}
                    </Box>
                  </Tooltip>
                </th>
              )}
              {isColumnVisible("paydown") && (
                <th
                  style={{
                    padding: "6px 4px",
                    borderBottom: isDarkMode
                      ? "1px solid rgba(255,255,255,0.1)"
                      : "1px solid #e2e8f0",
                    color: isDarkMode ? "rgba(255,255,255,0.9)" : "inherit",
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                    width: "7%",
                  }}
                  onClick={() => handleSort("paydownAmount")}
                >
                  <Tooltip title="Paydown" arrow>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <TrendingDownIcon fontSize="small" />
                      {renderSortIndicator("paydownAmount")}
                    </Box>
                  </Tooltip>
                </th>
              )}
              {isColumnVisible("monthly") && (
                <th
                  style={{
                    padding: "6px 4px",
                    borderBottom: isDarkMode
                      ? "1px solid rgba(255,255,255,0.1)"
                      : "1px solid #e2e8f0",
                    color: isDarkMode ? "rgba(255,255,255,0.9)" : "inherit",
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                    width: "7%",
                  }}
                  onClick={() => handleSort("maxMonthlyPayment")}
                >
                  <Tooltip title="Monthly Payment" arrow>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <CalendarMonthIcon fontSize="small" />
                      {renderSortIndicator("maxMonthlyPayment")}
                    </Box>
                  </Tooltip>
                </th>
              )}
              {isColumnVisible("dueDate") && (
                <th
                  style={{
                    padding: "6px 4px",
                    borderBottom: isDarkMode
                      ? "1px solid rgba(255,255,255,0.1)"
                      : "1px solid #e2e8f0",
                    color: isDarkMode ? "rgba(255,255,255,0.9)" : "inherit",
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                    width: "5%",
                  }}
                  onClick={() => handleSort("dueDate")}
                >
                  <Tooltip title="Due Date" arrow>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <EventIcon fontSize="small" />
                      {renderSortIndicator("dueDate")}
                    </Box>
                  </Tooltip>
                </th>
              )}
              {isColumnVisible("dateBorrowed") && (
                <th
                  style={{
                    padding: "6px 4px",
                    borderBottom: isDarkMode
                      ? "1px solid rgba(255,255,255,0.1)"
                      : "1px solid #e2e8f0",
                    color: isDarkMode ? "rgba(255,255,255,0.9)" : "inherit",
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                    width: "5%",
                  }}
                  onClick={() => handleSort("dateBorrowed")}
                >
                  <Tooltip title="Date Borrowed" arrow>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <CalendarMonthIcon fontSize="small" />
                      {renderSortIndicator("dateBorrowed")}
                    </Box>
                  </Tooltip>
                </th>
              )}
              {isColumnVisible("credits") && (
                <th
                  style={{
                    padding: "6px 4px",
                    borderBottom: isDarkMode
                      ? "1px solid rgba(255,255,255,0.1)"
                      : "1px solid #e2e8f0",
                    color: isDarkMode ? "rgba(255,255,255,0.9)" : "inherit",
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                    width: "6%",
                  }}
                  onClick={() => handleSort("statementCredits")}
                >
                  <Tooltip title="Credits" arrow>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <CardGiftcardIcon fontSize="small" />
                      {renderSortIndicator("statementCredits")}
                    </Box>
                  </Tooltip>
                </th>
              )}
              {isColumnVisible("payoffStrategy") && (
                <th
                  style={{
                    padding: "6px 4px",
                    borderBottom: isDarkMode
                      ? "1px solid rgba(255,255,255,0.1)"
                      : "1px solid #e2e8f0",
                    color: isDarkMode ? "rgba(255,255,255,0.9)" : "inherit",
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                    width: "10%",
                  }}
                  onClick={() => handleSort("payoffStrategy")}
                >
                  <Tooltip title="Payoff Strategy" arrow>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <RouteIcon fontSize="small" />
                      {renderSortIndicator("payoffStrategy")}
                    </Box>
                  </Tooltip>
                </th>
              )}
              {isColumnVisible("creditAffecting") && (
                <th
                  style={{
                    padding: "6px 4px",
                    borderBottom: isDarkMode
                      ? "1px solid rgba(255,255,255,0.1)"
                      : "1px solid #e2e8f0",
                    color: isDarkMode ? "rgba(255,255,255,0.9)" : "inherit",
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                    width: "10%",
                  }}
                  onClick={() => handleSort("creditAffecting")}
                >
                  <Tooltip title="Credit Affecting" arrow>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <AssessmentIcon fontSize="small" />
                      {renderSortIndicator("creditAffecting")}
                    </Box>
                  </Tooltip>
                </th>
              )}
              {isColumnVisible("actions") && (
                <th
                  style={{
                    padding: "6px 4px",
                    borderBottom: isDarkMode
                      ? "1px solid rgba(255,255,255,0.1)"
                      : "1px solid #e2e8f0",
                    color: isDarkMode ? "rgba(255,255,255,0.9)" : "inherit",
                    whiteSpace: "nowrap",
                    width: "130px",
                    minWidth: "130px",
                  }}
                >
                  <Tooltip title="Actions" arrow>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <MoreHorizIcon fontSize="small" />
                    </Box>
                  </Tooltip>
                </th>
              )}
            </tr>
            {/* Totals Row */}
            <tr
              style={{
                backgroundColor: isDarkMode ? "#2d2d2d" : "#f0f0f0",
                fontWeight: 600,
                fontSize: "11px",
              }}
            >
              {/* Status - empty */}
              {isColumnVisible("status") && (
                <td style={{ padding: "4px" }}></td>
              )}
              {/* Type - empty */}
              {isColumnVisible("type") && <td style={{ padding: "4px" }}></td>}
              {/* Quality - empty */}
              {isColumnVisible("quality") && (
                <td style={{ padding: "4px" }}></td>
              )}
              {/* Can Default - empty */}
              {isColumnVisible("canDefault") && (
                <td style={{ padding: "4px" }}></td>
              )}
              {/* Creditor - empty */}
              {isColumnVisible("creditor") && (
                <td style={{ padding: "4px" }}></td>
              )}
              {/* Account - empty */}
              {isColumnVisible("account") && (
                <td style={{ padding: "4px" }}></td>
              )}
              {/* Balance - total */}
              {isColumnVisible("balance") && (
                <td
                  style={{
                    padding: "4px",
                    whiteSpace: "nowrap",
                    color: getOutflowColor(totalBalanceAll),
                  }}
                >
                  {renderTotalsValue(formatOutflow(totalBalanceAll), 90)}
                </td>
              )}
              {/* Limit - total */}
              {isColumnVisible("limit") && (
                <td
                  style={{
                    padding: "4px",
                    whiteSpace: "nowrap",
                    color: totalCreditLimit > 0 ? positiveColor : "inherit",
                  }}
                >
                  {renderTotalsValue(
                    totalCreditLimit > 0
                      ? formatCurrency(totalCreditLimit)
                      : "—",
                    80
                  )}
                </td>
              )}
              {/* Available - total */}
              {isColumnVisible("available") && (
                <td
                  style={{
                    padding: "4px",
                    whiteSpace: "nowrap",
                    color: totalAvailable > 0 ? positiveColor : "inherit",
                  }}
                >
                  {renderTotalsValue(
                    totalAvailable > 0 ? formatCurrency(totalAvailable) : "—",
                    80
                  )}
                </td>
              )}
              {/* Utilization - average */}
              {isColumnVisible("utilization") && (
                <td style={{ padding: "4px", minWidth: "120px" }}>
                  {renderTotalsValue(
                    <Box
                      sx={{
                        display: "flex",
                        height: "18px",
                        borderRadius: "9px",
                        overflow: "hidden",
                      }}
                    >
                      <Box
                        sx={{
                          width: `${Math.max(
                            Math.min(averageUtilization, 100),
                            15
                          )}%`,
                          backgroundColor: negativeColor,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          minWidth: "30px",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "10px",
                            color: "white",
                            fontWeight: 600,
                          }}
                        >
                          {averageUtilization}%
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          flex: 1,
                          backgroundColor: positiveColor,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          minWidth: "30px",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "10px",
                            color: "white",
                            fontWeight: 600,
                          }}
                        >
                          {100 - Math.min(averageUtilization, 100)}%
                        </Typography>
                      </Box>
                    </Box>,
                    120
                  )}
                </td>
              )}
              {/* Paydown - total */}
              {isColumnVisible("paydown") && (
                <td
                  style={{
                    padding: "4px",
                    whiteSpace: "nowrap",
                    color: getOutflowColor(totalPaydownAmount),
                  }}
                >
                  {renderTotalsValue(formatOutflow(totalPaydownAmount), 90)}
                </td>
              )}
              {/* Monthly - total */}
              {isColumnVisible("monthly") && (
                <td
                  style={{
                    padding: "4px",
                    whiteSpace: "nowrap",
                    color: getOutflowColor(totalMonthlyPayment),
                  }}
                >
                  {renderTotalsValue(formatOutflow(totalMonthlyPayment), 90)}
                </td>
              )}
              {/* Due - empty */}
              {isColumnVisible("dueDate") && (
                <td style={{ padding: "4px" }}></td>
              )}
              {/* Date Borrowed - empty */}
              {isColumnVisible("dateBorrowed") && (
                <td style={{ padding: "4px" }}></td>
              )}
              {/* Credits - total */}
              {isColumnVisible("credits") && (
                <td style={{ padding: "4px", whiteSpace: "nowrap" }}>
                  {renderTotalsValue(
                    totalCredits > 0 ? formatCurrency(totalCredits) : "—",
                    70
                  )}
                </td>
              )}
              {/* Payoff Strategy - empty */}
              {isColumnVisible("payoffStrategy") && (
                <td style={{ padding: "4px" }}></td>
              )}
              {/* Credit Affecting - empty */}
              {isColumnVisible("creditAffecting") && (
                <td style={{ padding: "4px" }}></td>
              )}
              {/* Actions - empty */}
              {isColumnVisible("actions") && (
                <td style={{ padding: "4px" }}></td>
              )}
            </tr>
          </thead>
          <tbody>
            {groupByCreditor
              ? // Grouped view
                groupedDebts.map((group) => {
                  const isExpanded = expandedCreditors.has(group.creditor);
                  const hasMultipleDebts = group.debts.length > 1;
                  const groupHasPlaid = group.debts.some((debt) =>
                    isPlaidAccount(debt)
                  );
                  const renderGroupValue = (
                    content: React.ReactNode,
                    width: number
                  ) =>
                    plaidLoading && groupHasPlaid
                      ? renderPlaidPlaceholder(width)
                      : content;

                  // Render debt row helper
                  const renderDebtRow = (debt: Debt, isGroupChild: boolean) => {
                    const utilization =
                      Number(debt.creditLimit || 0) > 0
                        ? Math.round(
                            (Math.abs(Number(debt.balance || 0)) /
                              Number(debt.creditLimit || 0)) *
                              100
                          )
                        : 0;

                    return (
                      <tr
                        key={debt.id}
                        style={{
                          borderBottom: isDarkMode
                            ? "1px solid rgba(255,255,255,0.1)"
                            : "1px solid #e2e8f0",
                          color: isDarkMode
                            ? "rgba(255,255,255,0.9)"
                            : "inherit",
                          transition: "background-color 0.2s ease",
                          cursor: "pointer",
                          backgroundColor: isGroupChild
                            ? isDarkMode
                              ? "rgba(255,255,255,0.08)"
                              : "rgba(0,0,0,0.03)"
                            : "transparent",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = isDarkMode
                            ? "#2d2d2d"
                            : "#f7fafc";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = isGroupChild
                            ? isDarkMode
                              ? "rgba(255,255,255,0.08)"
                              : "rgba(0,0,0,0.03)"
                            : "transparent";
                        }}
                      >
                        {/* Status */}
                        {isColumnVisible("status") && (
                          <td
                            style={{
                              padding: "6px 4px",
                              paddingLeft: isGroupChild ? "28px" : "12px",
                            }}
                          >
                            <EditableCell
                              value={debt.status}
                              type="select"
                              options={STATUS_OPTIONS}
                              onSave={(value) =>
                                handleInlineUpdate(debt, "status", value)
                              }
                              disabled={!onUpdateDebt || isPlaidAccount(debt)}
                              isDarkMode={isDarkMode}
                              formatDisplay={(val) => {
                                const status = String(val || "Active");
                                switch (status) {
                                  case "Active":
                                    return (
                                      <Tooltip title="Active">
                                        <PlayArrowIcon
                                          sx={{
                                            fontSize: 18,
                                            color: "#38a169",
                                          }}
                                        />
                                      </Tooltip>
                                    );
                                  case "Paid Off":
                                    return (
                                      <Tooltip title="Paid Off">
                                        <TaskAltIcon
                                          sx={{
                                            fontSize: 18,
                                            color: "#3182ce",
                                          }}
                                        />
                                      </Tooltip>
                                    );
                                  case "Closed":
                                    return (
                                      <Tooltip title="Closed">
                                        <BlockIcon
                                          sx={{
                                            fontSize: 18,
                                            color: "#718096",
                                          }}
                                        />
                                      </Tooltip>
                                    );
                                  case "Frozen":
                                    return (
                                      <Tooltip title="Frozen">
                                        <AcUnitIcon
                                          sx={{
                                            fontSize: 18,
                                            color: "#63b3ed",
                                          }}
                                        />
                                      </Tooltip>
                                    );
                                  default:
                                    return status;
                                }
                              }}
                            />
                          </td>
                        )}
                        {/* Type */}
                        {isColumnVisible("type") && (
                          <td style={{ padding: "6px 4px" }}>
                            <EditableCell
                              value={debt.type || "Other"}
                              type="select"
                              options={TYPE_OPTIONS}
                              onSave={(value) =>
                                handleInlineUpdate(debt, "type", value)
                              }
                              disabled={!onUpdateDebt || isPlaidAccount(debt)}
                              isDarkMode={isDarkMode}
                            />
                          </td>
                        )}
                        {/* Debt Quality */}
                        {isColumnVisible("quality") && (
                          <td style={{ padding: "6px 4px" }}>
                            <EditableCell
                              value={
                                debt.debtQuality ??
                                (debt as unknown as { debt_quality?: string })
                                  .debt_quality ??
                                ""
                              }
                              type="select"
                              options={DEBT_QUALITY_OPTIONS}
                              onSave={(value) => {
                                const qualityValue =
                                  value === "" ? "" : (value as string);
                                handleInlineUpdate(
                                  debt,
                                  "debtQuality",
                                  qualityValue
                                );
                              }}
                              disabled={!onUpdateDebt}
                              isDarkMode={isDarkMode}
                              formatDisplay={(val) => {
                                if (!val || val === "") return "-";
                                return val === "good" ? (
                                  <Tooltip title="Good">
                                    <ThumbUpIcon
                                      sx={{ fontSize: 18, color: "#38a169" }}
                                    />
                                  </Tooltip>
                                ) : (
                                  <Tooltip title="Bad">
                                    <ThumbDownIcon
                                      sx={{ fontSize: 18, color: "#e53e3e" }}
                                    />
                                  </Tooltip>
                                );
                              }}
                            />
                          </td>
                        )}
                        {/* Can Default */}
                        {isColumnVisible("canDefault") && (
                          <td style={{ padding: "6px 4px" }}>
                            <EditableCell
                              value={debt.canDefault ? "can" : "cant"}
                              type="select"
                              options={CAN_DEFAULT_OPTIONS}
                              onSave={(value) =>
                                handleInlineUpdate(
                                  debt,
                                  "canDefault",
                                  String(value) === "can"
                                )
                              }
                              disabled={!onUpdateDebt}
                              isDarkMode={isDarkMode}
                              formatDisplay={(val) =>
                                String(val) === "can" ? "Can" : "Can't"
                              }
                            />
                          </td>
                        )}
                        {/* Creditor */}
                        {isColumnVisible("creditor") && (
                          <td
                            style={{
                              padding: "6px 4px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            <EditableCell
                              value={debt.creditor || ""}
                              type="text"
                              onSave={(value) =>
                                handleInlineUpdate(debt, "creditor", value)
                              }
                              disabled={!onUpdateDebt}
                              isDarkMode={isDarkMode}
                              formatDisplay={(val) => String(val) || "-"}
                            />
                          </td>
                        )}
                        {/* Account */}
                        {isColumnVisible("account") && (
                          <td
                            style={{
                              padding: "6px 4px",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            <EditableCell
                              value={
                                isPlaidAccount(debt)
                                  ? getPlaidDisplayName(debt)
                                  : debt.account
                              }
                              type="text"
                              onSave={(value) =>
                                isPlaidAccount(debt)
                                  ? void handleSavePlaidDisplayName(debt, value)
                                  : handleInlineUpdate(debt, "account", value)
                              }
                              disabled={!onUpdateDebt}
                              isDarkMode={isDarkMode}
                            />
                            {debt.createdVia && (
                              <CreatedViaBadge createdVia={debt.createdVia} />
                            )}
                            {isPlaidAccount(debt) &&
                            getPlaidDisplayName(debt) !== debt.account ? (
                              <Typography
                                component="div"
                                sx={{
                                  fontSize: "10px",
                                  color: "text.secondary",
                                  opacity: 0.8,
                                  lineHeight: 1.2,
                                  mt: 0.25,
                                }}
                              >
                                {debt.account}
                              </Typography>
                            ) : null}
                            {isPlaidAccount(debt) ? (
                              <span
                                style={{
                                  marginLeft: "8px",
                                  display: "inline-block",
                                  verticalAlign: "middle",
                                }}
                                title="Connected via Plaid"
                              >
                                <svg
                                  width="16"
                                  height="16"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                  style={{ display: "block" }}
                                >
                                  <rect
                                    width="24"
                                    height="24"
                                    rx="4"
                                    fill="#000000"
                                  />
                                  <path
                                    d="M12 4L4 8v8l8 4 8-4V8l-8-4z"
                                    fill="#00D4FF"
                                  />
                                  <path
                                    d="M12 12L8 10v4l4 2 4-2v-4l-4 2z"
                                    fill="#FFFFFF"
                                  />
                                </svg>
                              </span>
                            ) : null}
                            {hasLinkedExpense(debt) && (
                              <Tooltip title="View linked expense" arrow>
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenLinkedExpense(debt);
                                  }}
                                  sx={{
                                    marginLeft: "4px",
                                    padding: "2px",
                                    color: "#805AD5",
                                    "&:hover": { backgroundColor: "#805AD520" },
                                  }}
                                >
                                  <LinkIcon sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </td>
                        )}
                        {/* Balance */}
                        {isColumnVisible("balance") && (
                          <td
                            style={{
                              padding: "6px 4px",
                              whiteSpace: "nowrap",
                              color: getOutflowColor(debt.balance),
                            }}
                          >
                            {renderPlaidValue(
                              debt,
                              <EditableCell
                                value={debt.balance}
                                type="currency"
                                onSave={(value) =>
                                  handleInlineUpdate(debt, "balance", value)
                                }
                                disabled={!onUpdateDebt || isPlaidAccount(debt)}
                                isDarkMode={isDarkMode}
                                formatDisplay={(val) => formatOutflow(val)}
                              />,
                              90
                            )}
                          </td>
                        )}
                        {/* Credit Limit */}
                        {isColumnVisible("limit") && (
                          <td
                            style={{
                              padding: "6px 4px",
                              whiteSpace: "nowrap",
                              color:
                                Number(debt.creditLimit || 0) > 0
                                  ? positiveColor
                                  : "inherit",
                            }}
                          >
                            {renderPlaidValue(
                              debt,
                              <EditableCell
                                value={debt.creditLimit}
                                type="currency"
                                onSave={(value) =>
                                  handleInlineUpdate(debt, "creditLimit", value)
                                }
                                disabled={!onUpdateDebt || isPlaidAccount(debt)}
                                isDarkMode={isDarkMode}
                                formatDisplay={(val) =>
                                  Number(val || 0) > 0
                                    ? formatCurrency(Number(val || 0))
                                    : "—"
                                }
                              />,
                              80
                            )}
                          </td>
                        )}
                        {/* Available Credit */}
                        {isColumnVisible("available") && (
                          <td
                            style={{
                              padding: "6px 4px",
                              whiteSpace: "nowrap",
                              color:
                                Math.max(
                                  0,
                                  Number(debt.creditLimit || 0) -
                                    Number(debt.balance || 0)
                                ) > 0
                                  ? positiveColor
                                  : "inherit",
                            }}
                          >
                            {renderPlaidValue(
                              debt,
                              Math.max(
                                0,
                                Number(debt.creditLimit || 0) -
                                  Number(debt.balance || 0)
                              ) > 0
                                ? formatCurrency(
                                    Math.max(
                                      0,
                                      Number(debt.creditLimit || 0) -
                                        Number(debt.balance || 0)
                                    )
                                  )
                                : "—",
                              80
                            )}
                          </td>
                        )}
                        {/* Utilization */}
                        {isColumnVisible("utilization") && (
                          <td style={{ padding: "6px 4px", minWidth: "120px" }}>
                            {renderPlaidValue(
                              debt,
                              Number(debt.creditLimit || 0) > 0 ? (
                                <Box
                                  sx={{
                                    display: "flex",
                                    height: "18px",
                                    borderRadius: "9px",
                                    overflow: "hidden",
                                  }}
                                >
                                  <Box
                                    sx={{
                                      width: `${Math.max(
                                        Math.min(utilization, 100),
                                        15
                                      )}%`,
                                      backgroundColor: negativeColor,
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      minWidth: "30px",
                                    }}
                                  >
                                    <Typography
                                      sx={{
                                        fontSize: "10px",
                                        color: "white",
                                        fontWeight: 600,
                                      }}
                                    >
                                      {utilization}%
                                    </Typography>
                                  </Box>
                                  <Box
                                    sx={{
                                      flex: 1,
                                      backgroundColor: positiveColor,
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      minWidth: "30px",
                                    }}
                                  >
                                    <Typography
                                      sx={{
                                        fontSize: "10px",
                                        color: "white",
                                        fontWeight: 600,
                                      }}
                                    >
                                      {100 - Math.min(utilization, 100)}%
                                    </Typography>
                                  </Box>
                                </Box>
                              ) : (
                                <Typography
                                  sx={{
                                    fontSize: "11px",
                                    color: "text.secondary",
                                    textAlign: "center",
                                  }}
                                >
                                  —
                                </Typography>
                              ),
                              120
                            )}
                          </td>
                        )}
                        {/* Paydown */}
                        {isColumnVisible("paydown") && (
                          <td
                            style={{
                              padding: "6px 4px",
                              whiteSpace: "nowrap",
                              color: getOutflowColor(
                                debt.paydownAmount !== undefined &&
                                  debt.paydownAmount !== null
                                  ? debt.paydownAmount
                                  : calculateDefaultPaydown(debt)
                              ),
                            }}
                          >
                            {renderPlaidValue(
                              debt,
                              <EditableCell
                                value={
                                  debt.paydownAmount !== undefined &&
                                  debt.paydownAmount !== null
                                    ? debt.paydownAmount
                                    : calculateDefaultPaydown(debt)
                                }
                                type="currency"
                                onSave={(value) =>
                                  handleInlineUpdate(
                                    debt,
                                    "paydownAmount",
                                    value
                                  )
                                }
                                disabled={!onUpdateDebt}
                                isDarkMode={isDarkMode}
                                formatDisplay={(val) => formatOutflow(val)}
                                onReset={() =>
                                  handleInlineUpdate(
                                    debt,
                                    "paydownAmount",
                                    calculateDefaultPaydown(debt)
                                  )
                                }
                                resetTooltip={`Reset to default (${(debt.type || "").toLowerCase().includes("credit card") ? "66% of limit" : "100% of balance"})`}
                              />,
                              90
                            )}
                          </td>
                        )}
                        {/* Monthly Payment */}
                        {isColumnVisible("monthly") && (
                          <td
                            style={{
                              padding: "6px 4px",
                              whiteSpace: "nowrap",
                              color: getOutflowColor(debt.maxMonthlyPayment),
                            }}
                          >
                            {renderPlaidValue(
                              debt,
                              <EditableCell
                                value={debt.maxMonthlyPayment}
                                type="currency"
                                onSave={(value) =>
                                  handleInlineUpdate(
                                    debt,
                                    "maxMonthlyPayment",
                                    value
                                  )
                                }
                                disabled={!onUpdateDebt || isPlaidAccount(debt)}
                                isDarkMode={isDarkMode}
                                formatDisplay={(val) => formatOutflow(val)}
                              />,
                              90
                            )}
                          </td>
                        )}
                        {/* Due Date */}
                        {isColumnVisible("dueDate") && (
                          <td style={{ padding: "6px 4px", color: "white" }}>
                            <EditableCell
                              value={debt.dueDate || ""}
                              type="text"
                              onSave={(value) => {
                                const parsed = parseDueDateInput(String(value));
                                if (parsed !== "") {
                                  handleInlineUpdate(debt, "dueDate", parsed);
                                }
                              }}
                              disabled={!onUpdateDebt || isPlaidAccount(debt)}
                              isDarkMode={isDarkMode}
                              formatDisplay={formatDueDate}
                            />
                          </td>
                        )}
                        {/* Date Borrowed */}
                        {isColumnVisible("dateBorrowed") && (
                          <td style={{ padding: "6px 4px" }}>
                            <EditableCell
                              value={debt.dateBorrowed || ""}
                              type="date"
                              onSave={(value) =>
                                handleInlineUpdate(debt, "dateBorrowed", value)
                              }
                              disabled={!onUpdateDebt || isPlaidAccount(debt)}
                              isDarkMode={isDarkMode}
                              formatDisplay={(val) =>
                                val
                                  ? new Date(String(val)).toLocaleDateString(
                                      "en-US",
                                      { timeZone: "UTC" }
                                    )
                                  : "—"
                              }
                            />
                          </td>
                        )}
                        {/* Credits */}
                        {isColumnVisible("credits") && (
                          <td
                            style={{
                              padding: "6px 4px",
                              whiteSpace: "nowrap",
                              minWidth: "120px",
                            }}
                          >
                            {renderPlaidValue(
                              debt,
                              <EditableCell
                                value={debt.statementCredits}
                                type="currency"
                                onSave={(value) =>
                                  handleInlineUpdate(
                                    debt,
                                    "statementCredits",
                                    value
                                  )
                                }
                                disabled={!onUpdateDebt || isPlaidAccount(debt)}
                                isDarkMode={isDarkMode}
                                formatDisplay={(val) =>
                                  Number(val || 0) > 0
                                    ? formatCurrency(Number(val || 0))
                                    : "—"
                                }
                              />,
                              70
                            )}
                          </td>
                        )}
                        {/* Payoff Strategy */}
                        {isColumnVisible("payoffStrategy") && (
                          <td style={{ padding: "6px 4px" }}>
                            <EditableCell
                              value={debt.payoffStrategy || ""}
                              type="select"
                              options={PAYOFF_STRATEGY_OPTIONS}
                              onSave={(value) =>
                                handleInlineUpdate(
                                  debt,
                                  "payoffStrategy",
                                  value
                                )
                              }
                              disabled={!onUpdateDebt}
                              isDarkMode={isDarkMode}
                              formatDisplay={(val) => String(val) || "-"}
                            />
                          </td>
                        )}
                        {/* Credit Affecting */}
                        {isColumnVisible("creditAffecting") && (
                          <td style={{ padding: "6px 4px" }}>
                            <EditableCell
                              value={debt.creditAffecting || ""}
                              type="select"
                              options={CREDIT_AFFECTING_OPTIONS}
                              onSave={(value) =>
                                handleInlineUpdate(
                                  debt,
                                  "creditAffecting",
                                  value
                                )
                              }
                              disabled={!onUpdateDebt}
                              isDarkMode={isDarkMode}
                              formatDisplay={(val) => String(val) || "-"}
                            />
                          </td>
                        )}
                        {/* Actions */}
                        {isColumnVisible("actions") && (
                          <td
                            style={{
                              padding: "6px 4px",
                              whiteSpace: "nowrap",
                              minWidth: "120px",
                            }}
                          >
                            {isPlaidAccount(debt) ? (
                              <span
                                style={{
                                  fontSize: "12px",
                                  color: isDarkMode ? "#9ca3af" : "#718096",
                                }}
                              >
                                Plaid Account
                              </span>
                            ) : (
                              <div
                                style={{
                                  display: "flex",
                                  gap: "2px",
                                  flexWrap: "nowrap",
                                }}
                              >
                                {isSharedDebt(debt) && (
                                  <Chip
                                    label="S"
                                    size="small"
                                    variant="outlined"
                                    title="Shared"
                                    sx={{
                                      height: 18,
                                      minWidth: 18,
                                      fontSize: "10px",
                                      "& .MuiChip-label": { px: 0.5 },
                                    }}
                                  />
                                )}
                                <Tooltip
                                  title={
                                    debt.linkedAccountId
                                      ? "Change Linked Account"
                                      : "Link to Account"
                                  }
                                >
                                  <IconButton
                                    size="small"
                                    onClick={() => onLinkAccount(debt)}
                                    sx={{
                                      color: debt.linkedAccountId
                                        ? "#38a169"
                                        : "#718096",
                                      "&:hover": {
                                        backgroundColor: debt.linkedAccountId
                                          ? "rgba(56, 161, 105, 0.1)"
                                          : "rgba(113, 128, 150, 0.1)",
                                      },
                                    }}
                                  >
                                    <LinkIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Edit">
                                  <IconButton
                                    size="small"
                                    onClick={() => onEditDebt(debt)}
                                    sx={{
                                      color: "#4299e1",
                                      "&:hover": {
                                        backgroundColor:
                                          "rgba(66, 153, 225, 0.1)",
                                      },
                                    }}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                {onShareDebt && (
                                  <Tooltip title="Share">
                                    <IconButton
                                      size="small"
                                      onClick={() => onShareDebt(debt)}
                                      sx={{
                                        color: "#38a169",
                                        "&:hover": {
                                          backgroundColor:
                                            "rgba(56, 161, 105, 0.1)",
                                        },
                                      }}
                                    >
                                      <ShareIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                <Tooltip title="Delete">
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      if (
                                        window.confirm(
                                          `Are you sure you want to delete "${debt.account}"?`
                                        )
                                      ) {
                                        onDeleteDebt(debt.id);
                                      }
                                    }}
                                    sx={{
                                      color: negativeColor,
                                      "&:hover": {
                                        backgroundColor:
                                          "rgba(229, 62, 62, 0.1)",
                                      },
                                    }}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </div>
                            )}
                          </td>
                        )}
                      </tr>
                    );
                  };

                  // For single debt groups, just render the debt row
                  if (!hasMultipleDebts) {
                    return renderDebtRow(group.debts[0], false);
                  }

                  // For multi-debt groups, render a collapsible group header + child rows
                  return (
                    <React.Fragment key={`group-${group.creditor}`}>
                      {/* Group Header Row */}
                      <tr
                        onClick={() => toggleCreditorExpansion(group.creditor)}
                        style={{
                          borderBottom: isDarkMode
                            ? "1px solid rgba(255,255,255,0.1)"
                            : "1px solid #e2e8f0",
                          backgroundColor: isDarkMode ? "#252525" : "#f8f9fa",
                          color: isDarkMode
                            ? "rgba(255,255,255,0.9)"
                            : "inherit",
                          cursor: "pointer",
                          fontWeight: 600,
                        }}
                      >
                        {/* Status - expand icon */}
                        {isColumnVisible("status") && (
                          <td style={{ padding: "6px 4px" }}>
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                              {isExpanded ? (
                                <KeyboardArrowDownIcon sx={{ fontSize: 20 }} />
                              ) : (
                                <KeyboardArrowRightIcon sx={{ fontSize: 20 }} />
                              )}
                            </Box>
                          </td>
                        )}
                        {/* Type - count */}
                        {isColumnVisible("type") && (
                          <td style={{ padding: "6px 4px" }}>
                            <Chip
                              label={`${group.debts.length} items`}
                              size="small"
                              sx={{
                                fontSize: "11px",
                                height: "22px",
                                backgroundColor: isDarkMode
                                  ? "#3d3d3d"
                                  : "#e2e8f0",
                              }}
                            />
                          </td>
                        )}
                        {/* Quality - empty */}
                        {isColumnVisible("quality") && (
                          <td style={{ padding: "6px 4px" }}></td>
                        )}
                        {/* Can Default - empty */}
                        {isColumnVisible("canDefault") && (
                          <td style={{ padding: "6px 4px" }}></td>
                        )}
                        {/* Creditor */}
                        {isColumnVisible("creditor") && (
                          <td
                            style={{
                              padding: "6px 4px",
                              fontWeight: 600,
                            }}
                          >
                            {group.creditor}
                          </td>
                        )}
                        {/* Account - empty */}
                        {isColumnVisible("account") && (
                          <td style={{ padding: "6px 4px" }}></td>
                        )}
                        {/* Balance - total */}
                        {isColumnVisible("balance") && (
                          <td
                            style={{
                              padding: "6px 4px",
                              whiteSpace: "nowrap",
                              color: getOutflowColor(group.totalBalance),
                            }}
                          >
                            {renderGroupValue(
                              formatOutflow(group.totalBalance),
                              90
                            )}
                          </td>
                        )}
                        {/* Limit - total */}
                        {isColumnVisible("limit") && (
                          <td
                            style={{
                              padding: "6px 4px",
                              whiteSpace: "nowrap",
                              color:
                                group.totalCreditLimit > 0
                                  ? positiveColor
                                  : "inherit",
                            }}
                          >
                            {renderGroupValue(
                              group.totalCreditLimit > 0
                                ? formatCurrency(group.totalCreditLimit)
                                : "—",
                              80
                            )}
                          </td>
                        )}
                        {/* Available - total */}
                        {isColumnVisible("available") && (
                          <td
                            style={{
                              padding: "6px 4px",
                              whiteSpace: "nowrap",
                              color:
                                group.totalAvailable > 0
                                  ? positiveColor
                                  : "inherit",
                            }}
                          >
                            {renderGroupValue(
                              group.totalAvailable > 0
                                ? formatCurrency(group.totalAvailable)
                                : "—",
                              80
                            )}
                          </td>
                        )}
                        {/* Utilization - avg */}
                        {isColumnVisible("utilization") && (
                          <td style={{ padding: "6px 4px", minWidth: "120px" }}>
                            {renderGroupValue(
                              <Box
                                sx={{
                                  display: "flex",
                                  height: "18px",
                                  borderRadius: "9px",
                                  overflow: "hidden",
                                }}
                              >
                                <Box
                                  sx={{
                                    width: `${Math.max(
                                      Math.min(group.avgUtilization, 100),
                                      15
                                    )}%`,
                                    backgroundColor: negativeColor,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    minWidth: "30px",
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      fontSize: "10px",
                                      color: "white",
                                      fontWeight: 600,
                                    }}
                                  >
                                    {group.avgUtilization}%
                                  </Typography>
                                </Box>
                                <Box
                                  sx={{
                                    flex: 1,
                                    backgroundColor: positiveColor,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    minWidth: "30px",
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      fontSize: "10px",
                                      color: "white",
                                      fontWeight: 600,
                                    }}
                                  >
                                    {100 - Math.min(group.avgUtilization, 100)}%
                                  </Typography>
                                </Box>
                              </Box>,
                              120
                            )}
                          </td>
                        )}
                        {/* Paydown - total */}
                        {isColumnVisible("paydown") && (
                          <td
                            style={{
                              padding: "6px 4px",
                              whiteSpace: "nowrap",
                              color: getOutflowColor(group.totalPaydown),
                            }}
                          >
                            {renderGroupValue(
                              formatOutflow(group.totalPaydown),
                              90
                            )}
                          </td>
                        )}
                        {/* Monthly - total */}
                        {isColumnVisible("monthly") && (
                          <td
                            style={{
                              padding: "6px 4px",
                              whiteSpace: "nowrap",
                              color: getOutflowColor(group.totalMonthly),
                            }}
                          >
                            {renderGroupValue(
                              formatOutflow(group.totalMonthly),
                              90
                            )}
                          </td>
                        )}
                        {/* Due - empty */}
                        {isColumnVisible("dueDate") && (
                          <td style={{ padding: "6px 4px" }}></td>
                        )}
                        {/* Date Borrowed - empty */}
                        {isColumnVisible("dateBorrowed") && (
                          <td style={{ padding: "6px 4px" }}></td>
                        )}
                        {/* Credits - total */}
                        {isColumnVisible("credits") && (
                          <td
                            style={{
                              padding: "6px 4px",
                              whiteSpace: "nowrap",
                              minWidth: "120px",
                            }}
                          >
                            {renderGroupValue(
                              group.totalCredits > 0
                                ? formatCurrency(group.totalCredits)
                                : "—",
                              70
                            )}
                          </td>
                        )}
                        {/* Payoff Strategy - empty */}
                        {isColumnVisible("payoffStrategy") && (
                          <td style={{ padding: "6px 4px" }}></td>
                        )}
                        {/* Credit Affecting - empty */}
                        {isColumnVisible("creditAffecting") && (
                          <td style={{ padding: "6px 4px" }}></td>
                        )}
                        {/* Actions - share group */}
                        {isColumnVisible("actions") && (
                          <td
                            style={{ padding: "6px 4px" }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            {onShareDebt && (
                              <Tooltip title="Share Group">
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onShareDebt({
                                      ...group.debts[0],
                                      id: `group:${group.creditor}`,
                                      creditor: group.creditor,
                                    });
                                  }}
                                  sx={{
                                    color: "#718096",
                                    "&:hover": {
                                      backgroundColor:
                                        "rgba(113, 128, 150, 0.1)",
                                    },
                                  }}
                                >
                                  <ShareIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </td>
                        )}
                      </tr>
                      {/* Child Debt Rows (shown when expanded) */}
                      {isExpanded &&
                        group.debts.map((debt) => renderDebtRow(debt, true))}
                    </React.Fragment>
                  );
                })
              : // Flat view (no grouping)
                sortedDebts.map((debt) => {
                  const utilization =
                    Number(debt.creditLimit || 0) > 0
                      ? Math.round(
                          (Math.abs(Number(debt.balance || 0)) /
                            Number(debt.creditLimit || 0)) *
                            100
                        )
                      : 0;

                  return (
                    <tr
                      key={debt.id}
                      style={{
                        borderBottom: isDarkMode
                          ? "1px solid rgba(255,255,255,0.1)"
                          : "1px solid #e2e8f0",
                        color: isDarkMode ? "rgba(255,255,255,0.9)" : "inherit",
                        transition: "background-color 0.2s ease",
                        cursor: "pointer",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = isDarkMode
                          ? "#2d2d2d"
                          : "#f7fafc";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "transparent";
                      }}
                    >
                      {/* Status */}
                      {isColumnVisible("status") && (
                        <td style={{ padding: "6px 4px" }}>
                          <EditableCell
                            value={debt.status}
                            type="select"
                            options={STATUS_OPTIONS}
                            onSave={(value) =>
                              handleInlineUpdate(debt, "status", value)
                            }
                            disabled={!onUpdateDebt || isPlaidAccount(debt)}
                            isDarkMode={isDarkMode}
                            formatDisplay={(val) => {
                              const status = String(val || "Active");
                              switch (status) {
                                case "Active":
                                  return (
                                    <Tooltip title="Active">
                                      <PlayArrowIcon
                                        sx={{ fontSize: 18, color: "#38a169" }}
                                      />
                                    </Tooltip>
                                  );
                                case "Paid Off":
                                  return (
                                    <Tooltip title="Paid Off">
                                      <TaskAltIcon
                                        sx={{ fontSize: 18, color: "#3182ce" }}
                                      />
                                    </Tooltip>
                                  );
                                case "Closed":
                                  return (
                                    <Tooltip title="Closed">
                                      <BlockIcon
                                        sx={{ fontSize: 18, color: "#718096" }}
                                      />
                                    </Tooltip>
                                  );
                                case "Frozen":
                                  return (
                                    <Tooltip title="Frozen">
                                      <AcUnitIcon
                                        sx={{ fontSize: 18, color: "#63b3ed" }}
                                      />
                                    </Tooltip>
                                  );
                                default:
                                  return status;
                              }
                            }}
                          />
                        </td>
                      )}
                      {/* Type */}
                      {isColumnVisible("type") && (
                        <td style={{ padding: "6px 4px" }}>
                          <EditableCell
                            value={debt.type || "Other"}
                            type="select"
                            options={TYPE_OPTIONS}
                            onSave={(value) =>
                              handleInlineUpdate(debt, "type", value)
                            }
                            disabled={!onUpdateDebt || isPlaidAccount(debt)}
                            isDarkMode={isDarkMode}
                          />
                        </td>
                      )}
                      {/* Debt Quality */}
                      {isColumnVisible("quality") && (
                        <td style={{ padding: "6px 4px" }}>
                          <EditableCell
                            value={
                              debt.debtQuality ??
                              (debt as unknown as { debt_quality?: string })
                                .debt_quality ??
                              ""
                            }
                            type="select"
                            options={DEBT_QUALITY_OPTIONS}
                            onSave={(value) => {
                              const qualityValue =
                                value === "" ? "" : (value as string);
                              handleInlineUpdate(
                                debt,
                                "debtQuality",
                                qualityValue
                              );
                            }}
                            disabled={!onUpdateDebt}
                            isDarkMode={isDarkMode}
                            formatDisplay={(val) => {
                              if (!val || val === "") return "-";
                              return val === "good" ? (
                                <Tooltip title="Good">
                                  <ThumbUpIcon
                                    sx={{ fontSize: 18, color: "#38a169" }}
                                  />
                                </Tooltip>
                              ) : (
                                <Tooltip title="Bad">
                                  <ThumbDownIcon
                                    sx={{ fontSize: 18, color: "#e53e3e" }}
                                  />
                                </Tooltip>
                              );
                            }}
                          />
                        </td>
                      )}
                      {/* Can Default */}
                      {isColumnVisible("canDefault") && (
                        <td style={{ padding: "6px 4px" }}>
                          <EditableCell
                            value={debt.canDefault ? "can" : "cant"}
                            type="select"
                            options={CAN_DEFAULT_OPTIONS}
                            onSave={(value) =>
                              handleInlineUpdate(
                                debt,
                                "canDefault",
                                String(value) === "can"
                              )
                            }
                            disabled={!onUpdateDebt}
                            isDarkMode={isDarkMode}
                            formatDisplay={(val) =>
                              String(val) === "can" ? "Can" : "Can't"
                            }
                          />
                        </td>
                      )}
                      {/* Creditor */}
                      {isColumnVisible("creditor") && (
                        <td
                          style={{
                            padding: "6px 4px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          <EditableCell
                            value={debt.creditor || ""}
                            type="text"
                            onSave={(value) =>
                              handleInlineUpdate(debt, "creditor", value)
                            }
                            disabled={!onUpdateDebt}
                            isDarkMode={isDarkMode}
                            formatDisplay={(val) => String(val) || "-"}
                          />
                        </td>
                      )}
                      {/* Account */}
                      {isColumnVisible("account") && (
                        <td
                          style={{
                            padding: "6px 4px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          <EditableCell
                            value={
                              isPlaidAccount(debt)
                                ? getPlaidDisplayName(debt)
                                : debt.account
                            }
                            type="text"
                            onSave={(value) =>
                              isPlaidAccount(debt)
                                ? void handleSavePlaidDisplayName(debt, value)
                                : handleInlineUpdate(debt, "account", value)
                            }
                            disabled={!onUpdateDebt}
                            isDarkMode={isDarkMode}
                          />
                          {debt.createdVia && (
                            <CreatedViaBadge createdVia={debt.createdVia} />
                          )}
                          {isPlaidAccount(debt) &&
                          getPlaidDisplayName(debt) !== debt.account ? (
                            <Typography
                              component="div"
                              sx={{
                                fontSize: "10px",
                                color: "text.secondary",
                                opacity: 0.8,
                                lineHeight: 1.2,
                                mt: 0.25,
                              }}
                            >
                              {debt.account}
                            </Typography>
                          ) : null}
                          {isPlaidAccount(debt) ? (
                            <span
                              style={{
                                marginLeft: "8px",
                                display: "inline-block",
                                verticalAlign: "middle",
                              }}
                              title="Connected via Plaid"
                            >
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                style={{ display: "block" }}
                              >
                                <rect
                                  width="24"
                                  height="24"
                                  rx="4"
                                  fill="#000000"
                                />
                                <path
                                  d="M12 4L4 8v8l8 4 8-4V8l-8-4z"
                                  fill="#00D4FF"
                                />
                                <path
                                  d="M12 12L8 10v4l4 2 4-2v-4l-4 2z"
                                  fill="#FFFFFF"
                                />
                              </svg>
                            </span>
                          ) : null}
                          {hasLinkedExpense(debt) && (
                            <Tooltip title="View linked expense" arrow>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenLinkedExpense(debt);
                                }}
                                sx={{
                                  marginLeft: "4px",
                                  padding: "2px",
                                  color: "#805AD5",
                                  "&:hover": { backgroundColor: "#805AD520" },
                                }}
                              >
                                <LinkIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </td>
                      )}
                      {/* Balance */}
                      {isColumnVisible("balance") && (
                        <td
                          style={{
                            padding: "6px 4px",
                            whiteSpace: "nowrap",
                            color: getOutflowColor(debt.balance),
                          }}
                        >
                          {renderPlaidValue(
                            debt,
                            <EditableCell
                              value={debt.balance}
                              type="currency"
                              onSave={(value) =>
                                handleInlineUpdate(debt, "balance", value)
                              }
                              disabled={!onUpdateDebt || isPlaidAccount(debt)}
                              isDarkMode={isDarkMode}
                              formatDisplay={(val) => formatOutflow(val)}
                            />,
                            90
                          )}
                        </td>
                      )}
                      {/* Credit Limit */}
                      {isColumnVisible("limit") && (
                        <td
                          style={{
                            padding: "6px 4px",
                            whiteSpace: "nowrap",
                            color:
                              Number(debt.creditLimit || 0) > 0
                                ? positiveColor
                                : "inherit",
                          }}
                        >
                          {renderPlaidValue(
                            debt,
                            <EditableCell
                              value={debt.creditLimit}
                              type="currency"
                              onSave={(value) =>
                                handleInlineUpdate(debt, "creditLimit", value)
                              }
                              disabled={!onUpdateDebt || isPlaidAccount(debt)}
                              isDarkMode={isDarkMode}
                              formatDisplay={(val) =>
                                Number(val || 0) > 0
                                  ? formatCurrency(Number(val || 0))
                                  : "—"
                              }
                            />,
                            80
                          )}
                        </td>
                      )}
                      {/* Available Credit */}
                      {isColumnVisible("available") && (
                        <td
                          style={{
                            padding: "6px 4px",
                            whiteSpace: "nowrap",
                            color:
                              Math.max(
                                0,
                                Number(debt.creditLimit || 0) -
                                  Number(debt.balance || 0)
                              ) > 0
                                ? positiveColor
                                : "inherit",
                          }}
                        >
                          {renderPlaidValue(
                            debt,
                            Math.max(
                              0,
                              Number(debt.creditLimit || 0) -
                                Number(debt.balance || 0)
                            ) > 0
                              ? formatCurrency(
                                  Math.max(
                                    0,
                                    Number(debt.creditLimit || 0) -
                                      Number(debt.balance || 0)
                                  )
                                )
                              : "—",
                            80
                          )}
                        </td>
                      )}
                      {/* Utilization */}
                      {isColumnVisible("utilization") && (
                        <td style={{ padding: "6px 4px", minWidth: "120px" }}>
                          {renderPlaidValue(
                            debt,
                            Number(debt.creditLimit || 0) > 0 ? (
                              <Box
                                sx={{
                                  display: "flex",
                                  height: "18px",
                                  borderRadius: "9px",
                                  overflow: "hidden",
                                }}
                              >
                                <Box
                                  sx={{
                                    width: `${Math.max(
                                      Math.min(utilization, 100),
                                      15
                                    )}%`,
                                    backgroundColor: negativeColor,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    minWidth: "30px",
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      fontSize: "10px",
                                      color: "white",
                                      fontWeight: 600,
                                    }}
                                  >
                                    {utilization}%
                                  </Typography>
                                </Box>
                                <Box
                                  sx={{
                                    flex: 1,
                                    backgroundColor: positiveColor,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    minWidth: "30px",
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      fontSize: "10px",
                                      color: "white",
                                      fontWeight: 600,
                                    }}
                                  >
                                    {100 - Math.min(utilization, 100)}%
                                  </Typography>
                                </Box>
                              </Box>
                            ) : (
                              <Typography
                                sx={{
                                  fontSize: "11px",
                                  color: "text.secondary",
                                  textAlign: "center",
                                }}
                              >
                                —
                              </Typography>
                            ),
                            120
                          )}
                        </td>
                      )}
                      {/* Paydown */}
                      {isColumnVisible("paydown") && (
                        <td
                          style={{
                            padding: "6px 4px",
                            whiteSpace: "nowrap",
                            color: getOutflowColor(
                              debt.paydownAmount !== undefined &&
                                debt.paydownAmount !== null
                                ? debt.paydownAmount
                                : calculateDefaultPaydown(debt)
                            ),
                          }}
                        >
                          {renderPlaidValue(
                            debt,
                            <EditableCell
                              value={
                                debt.paydownAmount !== undefined &&
                                debt.paydownAmount !== null
                                  ? debt.paydownAmount
                                  : calculateDefaultPaydown(debt)
                              }
                              type="currency"
                              onSave={(value) =>
                                handleInlineUpdate(debt, "paydownAmount", value)
                              }
                              disabled={!onUpdateDebt}
                              isDarkMode={isDarkMode}
                              formatDisplay={(val) => formatOutflow(val)}
                              onReset={() =>
                                handleInlineUpdate(
                                  debt,
                                  "paydownAmount",
                                  calculateDefaultPaydown(debt)
                                )
                              }
                              resetTooltip={`Reset to default (${(debt.type || "").toLowerCase().includes("credit card") ? "66% of limit" : "100% of balance"})`}
                            />,
                            90
                          )}
                        </td>
                      )}
                      {/* Monthly Payment */}
                      {isColumnVisible("monthly") && (
                        <td
                          style={{
                            padding: "6px 4px",
                            whiteSpace: "nowrap",
                            color: getOutflowColor(debt.maxMonthlyPayment),
                          }}
                        >
                          {renderPlaidValue(
                            debt,
                            <EditableCell
                              value={debt.maxMonthlyPayment}
                              type="currency"
                              onSave={(value) =>
                                handleInlineUpdate(
                                  debt,
                                  "maxMonthlyPayment",
                                  value
                                )
                              }
                              disabled={!onUpdateDebt || isPlaidAccount(debt)}
                              isDarkMode={isDarkMode}
                              formatDisplay={(val) => formatOutflow(val)}
                            />,
                            90
                          )}
                        </td>
                      )}
                      {/* Due Date */}
                      {isColumnVisible("dueDate") && (
                        <td style={{ padding: "6px 4px", color: "white" }}>
                          <EditableCell
                            value={debt.dueDate || ""}
                            type="text"
                            onSave={(value) => {
                              const parsed = parseDueDateInput(String(value));
                              if (parsed !== "") {
                                handleInlineUpdate(debt, "dueDate", parsed);
                              }
                            }}
                            disabled={!onUpdateDebt || isPlaidAccount(debt)}
                            isDarkMode={isDarkMode}
                            formatDisplay={formatDueDate}
                          />
                        </td>
                      )}
                      {/* Date Borrowed */}
                      {isColumnVisible("dateBorrowed") && (
                        <td style={{ padding: "6px 4px" }}>
                          <EditableCell
                            value={debt.dateBorrowed || ""}
                            type="date"
                            onSave={(value) =>
                              handleInlineUpdate(debt, "dateBorrowed", value)
                            }
                            disabled={!onUpdateDebt || isPlaidAccount(debt)}
                            isDarkMode={isDarkMode}
                            formatDisplay={(val) =>
                              val
                                ? new Date(String(val)).toLocaleDateString(
                                    "en-US",
                                    { timeZone: "UTC" }
                                  )
                                : "—"
                            }
                          />
                        </td>
                      )}
                      {/* Credits */}
                      {isColumnVisible("credits") && (
                        <td
                          style={{
                            padding: "6px 4px",
                            whiteSpace: "nowrap",
                            minWidth: "120px",
                          }}
                        >
                          {renderPlaidValue(
                            debt,
                            <EditableCell
                              value={debt.statementCredits}
                              type="currency"
                              onSave={(value) =>
                                handleInlineUpdate(
                                  debt,
                                  "statementCredits",
                                  value
                                )
                              }
                              disabled={!onUpdateDebt || isPlaidAccount(debt)}
                              isDarkMode={isDarkMode}
                              formatDisplay={(val) =>
                                Number(val || 0) > 0
                                  ? formatCurrency(Number(val || 0))
                                  : "—"
                              }
                            />,
                            70
                          )}
                        </td>
                      )}
                      {/* Payoff Strategy */}
                      {isColumnVisible("payoffStrategy") && (
                        <td style={{ padding: "6px 4px" }}>
                          <EditableCell
                            value={debt.payoffStrategy || ""}
                            type="select"
                            options={PAYOFF_STRATEGY_OPTIONS}
                            onSave={(value) =>
                              handleInlineUpdate(debt, "payoffStrategy", value)
                            }
                            disabled={!onUpdateDebt}
                            isDarkMode={isDarkMode}
                            formatDisplay={(val) => String(val) || "-"}
                          />
                        </td>
                      )}
                      {/* Credit Affecting */}
                      {isColumnVisible("creditAffecting") && (
                        <td style={{ padding: "6px 4px" }}>
                          <EditableCell
                            value={debt.creditAffecting || ""}
                            type="select"
                            options={CREDIT_AFFECTING_OPTIONS}
                            onSave={(value) =>
                              handleInlineUpdate(debt, "creditAffecting", value)
                            }
                            disabled={!onUpdateDebt}
                            isDarkMode={isDarkMode}
                            formatDisplay={(val) => String(val) || "-"}
                          />
                        </td>
                      )}
                      {/* Actions */}
                      {isColumnVisible("actions") && (
                        <td
                          style={{
                            padding: "6px 4px",
                            whiteSpace: "nowrap",
                            minWidth: "120px",
                          }}
                        >
                          {isPlaidAccount(debt) ? (
                            <span
                              style={{
                                fontSize: "12px",
                                color: isDarkMode ? "#9ca3af" : "#718096",
                              }}
                            >
                              Plaid Account
                            </span>
                          ) : (
                            <div
                              style={{
                                display: "flex",
                                gap: "2px",
                                flexWrap: "nowrap",
                              }}
                            >
                              {isSharedDebt(debt) && (
                                <Chip
                                  label="Shared"
                                  size="small"
                                  variant="outlined"
                                  sx={{ height: 22 }}
                                />
                              )}
                              <Tooltip
                                title={
                                  debt.linkedAccountId
                                    ? "Change Linked Account"
                                    : "Link to Account"
                                }
                              >
                                <IconButton
                                  size="small"
                                  onClick={() => onLinkAccount(debt)}
                                  sx={{
                                    color: debt.linkedAccountId
                                      ? "#38a169"
                                      : "#718096",
                                    "&:hover": {
                                      backgroundColor: debt.linkedAccountId
                                        ? "rgba(56, 161, 105, 0.1)"
                                        : "rgba(113, 128, 150, 0.1)",
                                    },
                                  }}
                                >
                                  <LinkIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Edit">
                                <IconButton
                                  size="small"
                                  onClick={() => onEditDebt(debt)}
                                  sx={{
                                    color: "#4299e1",
                                    "&:hover": {
                                      backgroundColor:
                                        "rgba(66, 153, 225, 0.1)",
                                    },
                                  }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              {onShareDebt && (
                                <Tooltip title="Share">
                                  <IconButton
                                    size="small"
                                    onClick={() => onShareDebt(debt)}
                                    sx={{
                                      color: "#38a169",
                                      "&:hover": {
                                        backgroundColor:
                                          "rgba(56, 161, 105, 0.1)",
                                      },
                                    }}
                                  >
                                    <ShareIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}
                              <Tooltip title="Delete">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    if (
                                      window.confirm(
                                        `Are you sure you want to delete "${debt.account}"?`
                                      )
                                    ) {
                                      onDeleteDebt(debt.id);
                                    }
                                  }}
                                  sx={{
                                    color: negativeColor,
                                    "&:hover": {
                                      backgroundColor: "rgba(229, 62, 62, 0.1)",
                                    },
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </div>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
          </tbody>
        </table>
      </div>

      {/* Linked Expense Details Modal */}
      <Dialog
        open={!!linkedExpenseModal}
        onClose={() => setLinkedExpenseModal(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: isDarkMode ? "#1a1a1a" : "#fff",
            color: isDarkMode ? "#fff" : "#1a202c",
          },
        }}
      >
        <DialogTitle
          sx={{
            borderBottom: `1px solid ${isDarkMode ? "#333" : "#e2e8f0"}`,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <LinkIcon sx={{ color: "#805AD5" }} />
          Linked Expense Details
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {linkedExpenseModal && (
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 2,
                }}
              >
                <div>
                  <div
                    style={{
                      fontSize: "12px",
                      color: isDarkMode ? "#718096" : "#a0aec0",
                      marginBottom: "4px",
                    }}
                  >
                    Name
                  </div>
                  <div style={{ fontWeight: 600 }}>
                    {linkedExpenseModal.name}
                  </div>
                </div>
                <div>
                  <div
                    style={{
                      fontSize: "12px",
                      color: isDarkMode ? "#718096" : "#a0aec0",
                      marginBottom: "4px",
                    }}
                  >
                    Vendor
                  </div>
                  <div>{linkedExpenseModal.vendor || "—"}</div>
                </div>
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr 1fr",
                  gap: 2,
                }}
              >
                <div>
                  <div
                    style={{
                      fontSize: "12px",
                      color: isDarkMode ? "#718096" : "#a0aec0",
                      marginBottom: "4px",
                    }}
                  >
                    Amount
                  </div>
                  <div
                    style={{
                      fontWeight: 600,
                      color: "#805AD5",
                    }}
                  >
                    $
                    {Math.abs(Number(linkedExpenseModal.amount || 0)).toFixed(
                      2
                    )}
                  </div>
                </div>
                <div>
                  <div
                    style={{
                      fontSize: "12px",
                      color: isDarkMode ? "#718096" : "#a0aec0",
                      marginBottom: "4px",
                    }}
                  >
                    Due Date
                  </div>
                  <div>
                    {linkedExpenseModal.dueDate
                      ? new Date(linkedExpenseModal.dueDate).toLocaleDateString(
                          "en-US",
                          { timeZone: "UTC" }
                        )
                      : "—"}
                  </div>
                </div>
                <div>
                  <div
                    style={{
                      fontSize: "12px",
                      color: isDarkMode ? "#718096" : "#a0aec0",
                      marginBottom: "4px",
                    }}
                  >
                    Category
                  </div>
                  <div>{linkedExpenseModal.internalCategory || "—"}</div>
                </div>
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 2,
                }}
              >
                <div>
                  <div
                    style={{
                      fontSize: "12px",
                      color: isDarkMode ? "#718096" : "#a0aec0",
                      marginBottom: "4px",
                    }}
                  >
                    Status
                  </div>
                  <span
                    style={{
                      padding: "2px 8px",
                      borderRadius: "4px",
                      fontSize: "12px",
                      backgroundColor: linkedExpenseModal.isPaid
                        ? "#38A16920"
                        : "#D69E2E20",
                      color: linkedExpenseModal.isPaid ? "#38A169" : "#D69E2E",
                    }}
                  >
                    {linkedExpenseModal.isPaid ? "Paid" : "Unpaid"}
                  </span>
                </div>
                <div>
                  <div
                    style={{
                      fontSize: "12px",
                      color: isDarkMode ? "#718096" : "#a0aec0",
                      marginBottom: "4px",
                    }}
                  >
                    Recurring
                  </div>
                  <span
                    style={{
                      padding: "2px 8px",
                      borderRadius: "4px",
                      fontSize: "12px",
                      backgroundColor: linkedExpenseModal.isRecurring
                        ? "#3182CE20"
                        : isDarkMode
                          ? "#4a556820"
                          : "#e2e8f0",
                      color: linkedExpenseModal.isRecurring
                        ? "#3182CE"
                        : isDarkMode
                          ? "#a0aec0"
                          : "#718096",
                    }}
                  >
                    {linkedExpenseModal.isRecurring
                      ? linkedExpenseModal.recurrencePattern || "Yes"
                      : "One-time"}
                  </span>
                </div>
              </Box>

              {linkedExpenseModal.paymentAccount && (
                <div>
                  <div
                    style={{
                      fontSize: "12px",
                      color: isDarkMode ? "#718096" : "#a0aec0",
                      marginBottom: "4px",
                    }}
                  >
                    Payment Account
                  </div>
                  <div>{linkedExpenseModal.paymentAccount}</div>
                </div>
              )}

              {linkedExpenseModal.notes && (
                <Box
                  sx={{
                    backgroundColor: isDarkMode ? "#2d2d2d" : "#f7fafc",
                    borderRadius: "8px",
                    padding: "12px",
                  }}
                >
                  <div
                    style={{
                      fontSize: "12px",
                      color: isDarkMode ? "#718096" : "#a0aec0",
                      marginBottom: "4px",
                    }}
                  >
                    Notes
                  </div>
                  <div style={{ fontSize: "14px" }}>
                    {linkedExpenseModal.notes}
                  </div>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            borderTop: `1px solid ${isDarkMode ? "#333" : "#e2e8f0"}`,
            padding: "16px",
          }}
        >
          <Button
            onClick={() => setLinkedExpenseModal(null)}
            variant="contained"
            sx={{
              backgroundColor: "#805AD5",
              "&:hover": { backgroundColor: "#6B46C1" },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={minIncomeBreakdownOpen}
        onClose={() => setMinIncomeBreakdownOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          Min. Monthly Income Required
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr auto",
              rowGap: 1,
              columnGap: 2,
              fontSize: "14px",
            }}
          >
            <Typography>Expenses</Typography>
            <Typography sx={{ fontWeight: 700 }}>
              {formatCurrency(currentMonthlyRecurringExpensesTotal)}
            </Typography>

            <Typography>+ Flex Payments</Typography>
            <Typography sx={{ fontWeight: 700 }}>
              {formatCurrency(avgMonthlyPayoffOtherLoans24Months)}
            </Typography>

            <Typography>+ Standard Payments (Monthly)</Typography>
            <Typography sx={{ fontWeight: 700 }}>
              {formatCurrency(totalMonthlyPayment)}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMinIncomeBreakdownOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default DebtsList;
